/**
 * PreAnesthesiaAssessmentModal.js
 *
 * Implements the NABH & International Standard PRE-ANESTHESIA ASSESSMENT FORM & CLEARANCE
 * matching NABH COP.13 / CoC 13 and hospital MIS/LIS/RIS integration standards.
 *
 * Enhanced Workflow:
 * 1. Modal spans full screen (edge-to-edge clinical workspace).
 * 2. Stage 1 consists of 5 clinical subsections:
 *    - 1. Demographics & Baseline Vitals (Anthropometrics, Body Habitus, Baseline Vitals)
 *    - 2. Medical History & Habits (Previous Surgeries, Current Meds, Family Anesth Hx, Habits)
 *    - 3. Airway, Dental Chart & Systemic Reviews (Airway Metrics, 32-Tooth Interactive Chart, ROS)
 *    - 4. Lab & Radiology Orders & Results (Live CPOE order management & diagnostic results view)
 *    - 5. Anesthesia Plan, Cross-Specialty Consultation & Logistics (Techniques, Doctor Referral, Blood, ICU)
 * 3. Subsections with multiple sections are organized under collapsible Accordions:
 *    - Collapsed by default, with only the first subsection expanded.
 *    - "Expand All" and "Collapse All" toggle controls for fast navigation.
 * 4. Cross-Specialty Consultation:
 *    - Department & associated doctor picker.
 *    - Links referral via POST ipd_admission/<admissionId>/doctors/ with role: "referral_in_ipd".
 *    - Patient appears in doctor's Doctor Console under 'Referred Patients' (ReferredToMe.js).
 *    - Anesthesiologist's notes visible to both consulting doctor and attending nurses.
 * 5. Full NABH COP.13 / CoC 13 statutory consent and printable assessment sheets with official
 *    Hospital MIS header (logo, address, registration) and standard 2-column signature footer.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MedicationIcon from "@mui/icons-material/Medication";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ScienceIcon from "@mui/icons-material/Science";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SyncIcon from "@mui/icons-material/Sync";
import { toast } from "react-toastify";
import { fetchClinicalDataForPac } from "../utils/pacAutoPopulation";

import { fetchPac, recordPac } from "../api/otApi";
import { calculateBmi, getBmiCategory, getNextToothState } from "../utils/pacAssessment";
import { printAnesthesiaConsentForm, printPreAnesthesiaAssessmentForm } from "../utils/otPdf";
import { resolveLogo, resolveFacilityDetails } from "../../../utils/reportLetterhead";
import { resolveFacilityIdentity } from "../../inventory/utils/facilityIdentity";
import OrderManagementDrawer from "../../ipd/pages/doctor-console-ipd/OrderManagementDrawer";
import { drawerPatient } from "../pages/OtOrders";
import DentalAirwaySection from "./pac/DentalAirwaySection";
import PacCpoeSection from "./pac/PacCpoeSection";

const API_BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

// The admission whose records fill the assessment. Never the row's own id: that
// is an OT case or request number, and read as an admission it is another patient.
const admissionIdOf = (otCase) =>
  otCase?.admission_id || otCase?.admissionId || otCase?.open_admission_id;

const apiHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
  "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
  "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
  "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
  USERNAME: sessionStorage.getItem("userName") || "",
});

const ASA_OPTIONS = [
  { value: "I", label: "ASA I — Normal healthy patient" },
  { value: "II", label: "ASA II — Mild systemic disease (e.g. well-controlled HTN/DM)" },
  { value: "III", label: "ASA III — Severe systemic disease, functional limitation" },
  { value: "IV", label: "ASA IV — Severe systemic disease, constant threat to life" },
  { value: "V", label: "ASA V — Moribund patient, not expected to survive without surgery" },
  { value: "VI", label: "ASA VI — Declared brain-dead organ donor" },
];

const MALLAMPATI_OPTIONS = [
  { value: "Class I", desc: "Full soft palate, fauces, uvula, and pillars visible" },
  { value: "Class II", desc: "Soft palate, fauces, and portion of uvula visible" },
  { value: "Class III", desc: "Soft palate and base of uvula visible" },
  { value: "Class IV", desc: "Hard palate only visible" },
];

const ANESTHESIA_PLANS = [
  "General Anesthesia (GA)",
  "Spinal Anesthesia (SAB)",
  "Epidural Anesthesia",
  "Combined Spinal-Epidural (CSE)",
  "Regional Nerve Block",
  "Monitored Anesthesia Care (MAC)",
  "Local Anesthesia with Sedation",
  "Local Anesthesia Only",
];

const QUICK_PREMEDS = [
  "Tab Pantoprazole 40mg PO at 6:00 AM with sips of water",
  "Inj Cefazolin 1g IV on call to OT (within 60m of incision)",
  "Inj Ondansetron 4mg IV prior to OT shifting",
  "Nebulization with Budecort 0.5mg 2 hours prior to OT",
  "Tab Alprazolam 0.25mg PO at bedtime prior to surgery",
];


// `lockedReason` is set when the server would refuse a PAC outcome on this case
// (PreopPanel's PacCard decides). The form still opens so the assessment and the
// consent can be printed or exported; only saving is off, and the reason says why.
export default function PreAnesthesiaAssessmentModal({ otCase, onClose, onSaved, lockedReason = "" }) {
  // Stepper stage: 1 = Pre-Anesthesia Assessment Form, 2 = Fitness Outcome & Orders
  const [activeStage, setActiveStage] = useState(1);
  // 5 Subsections: 0: Demographics & Vitals, 1: History & Habits, 2: Airway/Dental/Systemic, 3: Lab & Radiology Orders (CPOE), 4: Anesthesia Plan & Logistics
  const [formSubTab, setFormSubTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);

  // --- Accordions Expanded State (Default: First subsection expanded, rest collapsed) ---
  const [expandedSec0, setExpandedSec0] = useState({ 0: true, 1: false });
  const [expandedSec1, setExpandedSec1] = useState({ 0: true, 1: false, 2: false, 3: false });
  const [expandedSec2, setExpandedSec2] = useState({ 0: true, 1: false, 2: false });
  const [expandedSec3, setExpandedSec3] = useState({ 0: true, 1: true });
  const [expandedSec4, setExpandedSec4] = useState({ 0: true, 1: false, 2: false, 3: false, 4: false });

  const handleExpandAll = (tabIndex) => {
    if (tabIndex === 0) setExpandedSec0({ 0: true, 1: true });
    else if (tabIndex === 1) setExpandedSec1({ 0: true, 1: true, 2: true, 3: true });
    else if (tabIndex === 2) setExpandedSec2({ 0: true, 1: true, 2: true });
    else if (tabIndex === 3) setExpandedSec3({ 0: true, 1: true });
    else if (tabIndex === 4) setExpandedSec4({ 0: true, 1: true, 2: true, 3: true, 4: true });
  };

  const handleCollapseAll = (tabIndex) => {
    if (tabIndex === 0) setExpandedSec0({ 0: false, 1: false });
    else if (tabIndex === 1) setExpandedSec1({ 0: false, 1: false, 2: false, 3: false });
    else if (tabIndex === 2) setExpandedSec2({ 0: false, 1: false, 2: false });
    else if (tabIndex === 3) setExpandedSec3({ 0: false, 1: false });
    else if (tabIndex === 4) setExpandedSec4({ 0: false, 1: false, 2: false, 3: false, 4: false });
  };

  const handleExpandAllGlobal = () => {
    setExpandedSec0({ 0: true, 1: true });
    setExpandedSec1({ 0: true, 1: true, 2: true, 3: true });
    setExpandedSec2({ 0: true, 1: true, 2: true });
    setExpandedSec3({ 0: true, 1: true });
    setExpandedSec4({ 0: true, 1: true, 2: true, 3: true, 4: true });
  };

  const handleCollapseAllGlobal = () => {
    setExpandedSec0({ 0: false, 1: false });
    setExpandedSec1({ 0: false, 1: false, 2: false, 3: false });
    setExpandedSec2({ 0: false, 1: false, 2: false });
    setExpandedSec3({ 0: false, 1: false });
    setExpandedSec4({ 0: false, 1: false, 2: false, 3: false, 4: false });
  };

  // --- Stage 1 Form State ---
  const [weightKg, setWeightKg] = useState(otCase?.weight || "");
  const [heightCm, setHeightCm] = useState(otCase?.height || "");
  const [diagnosis, setDiagnosis] = useState(otCase?.diagnosis || "");
  const [procedurePlanned, setProcedurePlanned] = useState(
    otCase?.procedure_name || otCase?.procedures?.[0]?.procedure_name || ""
  );
  const [procedureDate, setProcedureDate] = useState(
    otCase?.next_booking_start ? otCase.next_booking_start.substring(0, 10) : ""
  );
  const [department, setDepartment] = useState(otCase?.department_name || otCase?.department || "");
  const [bloodGroup, setBloodGroup] = useState(
    otCase?.blood_group || otCase?.blood_type || ""
  );

  // Medical History
  const [summaryHistory, setSummaryHistory] = useState("");
  const [previousSurgeries, setPreviousSurgeries] = useState([]);
  const [currentMeds, setCurrentMeds] = useState("");
  const [familyAnesthComp, setFamilyAnesthComp] = useState(false);
  const [familyAnesthCompDesc, setFamilyAnesthCompDesc] = useState("");

  // Habits & Allergies
  const [allergies, setAllergies] = useState(otCase?.known_allergies || "");
  const [alcohol, setAlcohol] = useState("NONE");
  const [smokingPacks, setSmokingPacks] = useState("");
  const [smokingYears, setSmokingYears] = useState("");
  const [smokingStopped, setSmokingStopped] = useState(false);
  const [drugAbuse, setDrugAbuse] = useState("");

  // Vitals & Airway
  const [tempC, setTempC] = useState("98.6");
  const [rrPerMin, setRrPerMin] = useState("16");
  const [bpSys, setBpSys] = useState("");
  const [bpDia, setBpDia] = useState("");
  const [pulsePerMin, setPulsePerMin] = useState("");
  const [spo2Percent, setSpo2Percent] = useState("98");
  const [fio2Percent, setFio2Percent] = useState("");
  const [pallor, setPallor] = useState("ABSENT");
  const [physicalNotes, setPhysicalNotes] = useState("");
  const [fastingHours, setFastingHours] = useState("");

  const [mouthOpening, setMouthOpening] = useState("ADEQUATE");
  const [mouthOpeningFingers, setMouthOpeningFingers] = useState("3");
  const [neckMovements, setNeckMovements] = useState("NORMAL");
  const [osa, setOsa] = useState("NIL");
  const [mallampati, setMallampati] = useState("Class I");
  const [thyromentalDistance, setThyromentalDistance] = useState("6.5");
  const [neckMass, setNeckMass] = useState(false);
  const [neckMassDesc, setNeckMassDesc] = useState("");

  // Dental Chart State: map of toothKey -> 'NORMAL' | 'MISSING' | 'LOOSE'
  const [dentalMarks, setDentalMarks] = useState({});
  const [dentures, setDentures] = useState(false);
  const [patientInformed, setPatientInformed] = useState(true);
  const [expectedDifficultAirway, setExpectedDifficultAirway] = useState(false);

  // Systemic Reviews
  const [neuroMental, setNeuroMental] = useState("NORMAL");
  const [neuroCva, setNeuroCva] = useState(false);
  const [neuroSeizures, setNeuroSeizures] = useState(false);
  const [neuroSyncope, setNeuroSyncope] = useState(false);
  const [neuroWeakness, setNeuroWeakness] = useState(false);
  const [neuroMyopathies, setNeuroMyopathies] = useState(false);
  const [neuroChronicPain, setNeuroChronicPain] = useState(false);
  const [neuroOthers, setNeuroOthers] = useState("");

  const [cvsNad, setCvsNad] = useState(true);
  const [cvsHtn, setCvsHtn] = useState(false);
  const [cvsHtnStatus, setCvsHtnStatus] = useState("WELL_CONTROLLED");
  const [cvsIhd, setCvsIhd] = useState(false);
  const [cvsArrhythmias, setCvsArrhythmias] = useState("");
  const [cvsHf, setCvsHf] = useState(false);
  const [cvsHfNyha, setCvsHfNyha] = useState("I");
  const [cvsDyslipidemia, setCvsDyslipidemia] = useState(false);
  const [cvsPalpitation, setCvsPalpitation] = useState(false);
  const [cvsLvef, setCvsLvef] = useState("60");
  const [cvsValvular, setCvsValvular] = useState("");
  const [cvsOthers, setCvsOthers] = useState("");
  const [cvsAuscHeart, setCvsAuscHeart] = useState("S1 S2 heard, no murmur");
  const [cvsAuscLungs, setCvsAuscLungs] = useState("B/L clear, vesicular breath sounds");

  const [respNad, setRespNad] = useState(true);
  const [respCopd, setRespCopd] = useState(false);
  const [respAsthma, setRespAsthma] = useState(false);
  const [respRecentUri, setRespRecentUri] = useState(false);
  const [respTb, setRespTb] = useState(false);
  const [respCough, setRespCough] = useState(false);
  const [respDyspnea, setRespDyspnea] = useState(false);
  const [respOthers, setRespOthers] = useState("");

  const [giNad, setGiNad] = useState(true);
  const [giGerd, setGiGerd] = useState(false);
  const [giJaundice, setGiJaundice] = useState(false);
  const [giCirrhosis, setGiCirrhosis] = useState(false);
  const [giOthers, setGiOthers] = useState("");

  const [renalNad, setRenalNad] = useState(true);
  const [renalCrf, setRenalCrf] = useState(false);
  const [renalCcr, setRenalCcr] = useState("");
  const [renalEsrd, setRenalEsrd] = useState(false);
  const [renalDialysis, setRenalDialysis] = useState(false);
  const [renalOthers, setRenalOthers] = useState("");

  const [endoNad, setEndoNad] = useState(true);
  const [endoDm, setEndoDm] = useState(false);
  const [endoDmType, setEndoDmType] = useState("TYPE_2");
  const [endoHba1c, setEndoHba1c] = useState("");
  const [endoThyroid, setEndoThyroid] = useState("NORMAL");
  const [endoAdrenal, setEndoAdrenal] = useState(false);
  const [endoLmp, setEndoLmp] = useState("");
  const [endoOthers, setEndoOthers] = useState("");

  // Backward-compatible manual lab fallbacks (if existing records exist)
  const [cbcDate, setCbcDate] = useState("");
  const [cbcWbc, setCbcWbc] = useState("");
  const [cbcHb, setCbcHb] = useState("");
  const [cbcHct, setCbcHct] = useState("");
  const [cbcPlt, setCbcPlt] = useState("");
  const [chemDate, setChemDate] = useState("");
  const [chemNa, setChemNa] = useState("");
  const [chemK, setChemK] = useState("");
  const [chemCl, setChemCl] = useState("");
  const [chemHco3, setChemHco3] = useState("");
  const [chemBun, setChemBun] = useState("");
  const [chemCr, setChemCr] = useState("");
  const [chemGlu, setChemGlu] = useState("");
  const [coagDate, setCoagDate] = useState("");
  const [coagPt, setCoagPt] = useState("");
  const [coagPtt, setCoagPtt] = useState("");
  const [coagAptt, setCoagAptt] = useState("");
  const [coagInr, setCoagInr] = useState("");
  const [cxrFindings, setCxrFindings] = useState("");
  const [ecgFindings, setEcgFindings] = useState("");
  const [otherInvestigations, setOtherInvestigations] = useState("");
  const [otherLabs, setOtherLabs] = useState("");

  // --- Subsection 4: CPOE Lab & Radiology Orders & Results State ---
  const [cpoeOrders, setCpoeOrders] = useState([]);
  const [loadingCpoeOrders, setLoadingCpoeOrders] = useState(false);
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);

  // --- Subsection 5: Cross-Specialty Consultation & Doctor Referral ---
  const [consultRequired, setConsultRequired] = useState(false);
  const [consultSpecialty, setConsultSpecialty] = useState("");
  const [consultAccepted, setConsultAccepted] = useState(true);
  const [consultReason, setConsultReason] = useState("");

  const [medicalDepartments, setMedicalDepartments] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [selectedConsultDept, setSelectedConsultDept] = useState("");
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedConsultDoctorId, setSelectedConsultDoctorId] = useState("");
  const [consultNotes, setConsultNotes] = useState("");
  const [referringInProgress, setReferringInProgress] = useState(false);
  const [activeReferrals, setActiveReferrals] = useState([]);

  // Anesthesia Plan & Logistics
  const [anesthesiaPlan, setAnesthesiaPlan] = useState("General Anesthesia (GA)");
  const [riskDiscussedWith, setRiskDiscussedWith] = useState("PATIENT");
  const [agreedToPlan, setAgreedToPlan] = useState(true);
  const [kinNameRelation, setKinNameRelation] = useState("");
  const [consentRiskLevel, setConsentRiskLevel] = useState("NORMAL");
  const [consentRiskDetails, setConsentRiskDetails] = useState("");

  const [bloodPrbc, setBloodPrbc] = useState("");
  const [bloodWb, setBloodWb] = useState("");
  const [bloodFfp, setBloodFfp] = useState("");
  const [bloodPlt, setBloodPlt] = useState("");
  const [bloodRemarks, setBloodRemarks] = useState("");
  const [icuBooking, setIcuBooking] = useState(false);

  // --- Stage 2 State: Fitness Outcome & Orders ---
  const [asaClass, setAsaClass] = useState("II");
  const [asaEmergency, setAsaEmergency] = useState(
    (otCase?.scheduling_type || "").toUpperCase() === "EMERGENCY"
  );
  const [outcome, setOutcome] = useState("FIT");
  const [nextReviewDate, setNextReviewDate] = useState("");
  const [notes, setNotes] = useState("");
  const [suggestedMeds, setSuggestedMeds] = useState(
    "Tab Pantoprazole 40mg PO at 6:00 AM\nInj Cefazolin 1g IV on call to OT"
  );
  const [optInstructions, setOptInstructions] = useState(
    "NPO after 12:00 Midnight (solids 6h, clear fluids up to 2h prior). Continue regular morning antihypertensive with sips of water."
  );
  // Empty until someone writes it: the pre-op tab reads any text here as the
  // day-of-surgery check having been done.
  const [reassessmentNotes, setReassessmentNotes] = useState("");
  const [syncingClinicalData, setSyncingClinicalData] = useState(false);
  const [autoPopulatedSummary, setAutoPopulatedSummary] = useState(null);

  // Auto-calculated BMI
  const computedBmi = useMemo(() => {
    return calculateBmi(weightKg, heightCm) || "";
  }, [weightKg, heightCm]);

  // Load CPOE Lab & Radiology orders and results live for this admission
  const loadCpoeOrders = useCallback(async () => {
    const admissionId = admissionIdOf(otCase);
    if (!admissionId) {
      setCpoeOrders([]);
      return;
    }
    setLoadingCpoeOrders(true);
    try {
      const [labRes, radRes] = await Promise.allSettled([
        axios.post(
          `${API_BASE}get_ipd_lab_orders_by_admission/`,
          { admission_id: String(admissionId) },
          { headers: apiHeaders() }
        ),
        axios.post(
          `${API_BASE}get_ipd_radiology_orders_by_admission/`,
          { admission_id: String(admissionId) },
          { headers: apiHeaders() }
        ),
      ]);

      const labOrders =
        labRes.status === "fulfilled"
          ? labRes.value?.data?.orders || labRes.value?.data?.data?.orders || []
          : [];
      const radOrders =
        radRes.status === "fulfilled"
          ? radRes.value?.data?.orders || radRes.value?.data?.data?.orders || []
          : [];

      const combined = [
        ...(Array.isArray(labOrders)
          ? labOrders.map((o, idx) => ({
              id: o.order_no || `lab-${o.medical_service_id || idx}`,
              order_no: o.order_no || `LAB-${idx + 1}`,
              type: "Laboratory",
              service_name: o.service_name || o.test_name || "Laboratory Investigation",
              doctor_name: o.doctor_name || otCase?.primary_surgeon_name || "Attending Physician",
              priority: o.priority || "Routine",
              ordered_time: o.ordered_time || o.created_at || "",
              status: o.status || o.lab_status || o.test_status || o.current_status || "Ordered",
              results: o.results || o.test_results || o.parameters || o.lab_results || [],
              notes: o.clinical_notes || o.instructions || "",
            }))
          : []),
        ...(Array.isArray(radOrders)
          ? radOrders.map((o, idx) => ({
              id: o.order_no || `rad-${o.medical_service_id || idx}`,
              order_no: o.order_no || `RAD-${idx + 1}`,
              type: "Radiology",
              service_name: o.service_name || o.test_name || "Radiology Imaging",
              doctor_name: o.doctor_name || otCase?.primary_surgeon_name || "Attending Physician",
              priority: o.priority || "Routine",
              ordered_time: o.ordered_time || o.created_at || "",
              status: o.status || o.current_status || "Ordered",
              results: o.results || o.report || o.findings || "",
              findings: o.findings || o.report_findings || "",
              impression: o.impression || o.report_impression || "",
              radiologist: o.radiologist_name || o.doctor_name || "",
              notes: o.clinical_notes || o.instructions || "",
            }))
          : []),
      ];

      setCpoeOrders(combined);
    } catch (err) {
      console.error("Failed to load CPOE orders:", err);
    } finally {
      setLoadingCpoeOrders(false);
    }
  }, [otCase]);

  useEffect(() => {
    loadCpoeOrders();
  }, [loadCpoeOrders]);

  // Load Medical Departments for Cross-Specialty Consultation
  const loadMedicalDepartments = useCallback(async () => {
    setLoadingDepts(true);
    try {
      const res = await axios.get(`${API_BASE}get_all_medical_departments/`, {
        headers: apiHeaders(),
      });
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setMedicalDepartments(list);
    } catch (err) {
      console.error("Failed to load medical departments:", err);
    } finally {
      setLoadingDepts(false);
    }
  }, []);

  useEffect(() => {
    loadMedicalDepartments();
  }, [loadMedicalDepartments]);

  const handleDeptChange = async (dept) => {
    setSelectedConsultDept(dept);
    setSelectedConsultDoctorId("");
    if (!dept) {
      setAvailableDoctors([]);
      return;
    }
    setLoadingDocs(true);
    try {
      const res = await axios.post(
        `${API_BASE}get_all_doctors_based_on_department/`,
        { department: dept, assigned_doctors_only: false },
        { headers: apiHeaders() }
      );
      const list = Array.isArray(res?.data?.doctors)
        ? res.data.doctors
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setAvailableDoctors(list);
    } catch (err) {
      console.error("Failed to load doctors for department:", err);
      setAvailableDoctors([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleRequestReferral = async () => {
    const admissionId = admissionIdOf(otCase);
    if (!admissionId) {
      toast.warning("Inpatient admission ID is required to link referral doctor.");
      return;
    }
    if (!selectedConsultDoctorId) {
      toast.warning("Please select a doctor to refer to.");
      return;
    }
    const doctorObj = availableDoctors.find(
      (d) => String(d.doctor_id || d.id) === String(selectedConsultDoctorId)
    );
    if (!doctorObj) {
      toast.warning("Selected doctor not found.");
      return;
    }
    setReferringInProgress(true);
    try {
      const docData = {
        doctor_id: doctorObj.doctor_id || doctorObj.id,
        role: "referral_in_ipd",
        specialty: doctorObj.specialty || selectedConsultDept || "",
        notes: consultNotes.trim(),
      };
      const res = await axios.post(
        `${API_BASE}ipd_admission/${admissionId}/doctors/`,
        docData,
        { headers: apiHeaders() }
      );
      if (res.status === 200 || res.status === 201) {
        toast.success(
          `Specialty clearance requested from Dr. ${doctorObj.name} (${selectedConsultDept}). Patient added to their Referred Patients queue.`
        );
        setActiveReferrals((prev) => [
          ...prev,
          {
            id: doctorObj.doctor_id || doctorObj.id,
            name: doctorObj.name,
            specialty: selectedConsultDept,
            notes: consultNotes.trim(),
            date: new Date().toISOString(),
            status: "Clearance Requested",
          },
        ]);
        setConsultRequired(true);
        setConsultSpecialty(selectedConsultDept);
        setConsultReason(consultNotes.trim());
      } else {
        toast.error("Failed to link referral doctor to admission.");
      }
    } catch (err) {
      console.error("Error adding referral doctor:", err);
      toast.error(err.response?.data?.message || "An error occurred while linking referral doctor.");
    } finally {
      setReferringInProgress(false);
    }
  };

  const applyAggregatedClinicalData = useCallback(
    (data, forceOverwrite = false) => {
      if (!data) return;

      // 1. Demographics & Vitals
      if (forceOverwrite || !bloodGroup) {
        if (data.bloodGroup || data.blood_group) setBloodGroup(data.bloodGroup || data.blood_group);
      }
      if (forceOverwrite || !weightKg) {
        if (data.vitals?.weightKg) setWeightKg(data.vitals.weightKg);
      }
      if (forceOverwrite || !heightCm) {
        if (data.vitals?.heightCm) setHeightCm(data.vitals.heightCm);
      }
      if (forceOverwrite || !pulsePerMin) {
        if (data.vitals?.pulsePerMin) setPulsePerMin(data.vitals.pulsePerMin);
      }
      if (forceOverwrite || (!bpSys && !bpDia)) {
        if (data.vitals?.bpSys) setBpSys(data.vitals.bpSys);
        if (data.vitals?.bpDia) setBpDia(data.vitals.bpDia);
      }
      if (forceOverwrite || !rrPerMin) {
        if (data.vitals?.rrPerMin) setRrPerMin(data.vitals.rrPerMin);
      }
      if (forceOverwrite || !spo2Percent) {
        if (data.vitals?.spo2Percent) setSpo2Percent(data.vitals.spo2Percent);
      }
      if (forceOverwrite || !tempC) {
        if (data.vitals?.tempC) setTempC(data.vitals.tempC);
      }
      if (forceOverwrite || !fio2Percent) {
        if (data.vitals?.fio2Percent) setFio2Percent(data.vitals.fio2Percent);
      }
      if (data.vitals?.neuroMental && (forceOverwrite || neuroMental === "NORMAL")) {
        setNeuroMental(data.vitals.neuroMental);
      }

      // 2. Medical History & Habits
      if (forceOverwrite || !summaryHistory) {
        if (data.history?.summaryHistory) setSummaryHistory(data.history.summaryHistory);
      }
      if (forceOverwrite || previousSurgeries.length === 0) {
        if (data.history?.previousSurgeries?.length > 0) {
          setPreviousSurgeries(data.history.previousSurgeries);
        }
      }
      if (forceOverwrite || !familyAnesthComp) {
        if (data.history?.familyAnesthComp) {
          setFamilyAnesthComp(true);
          setFamilyAnesthCompDesc(data.history.familyAnesthCompDesc);
        }
      }
      if (forceOverwrite || !smokingPacks) {
        if (data.history?.smokingPacks) {
          setSmokingPacks(data.history.smokingPacks);
          setSmokingStopped(data.history.smokingStopped);
        }
      }
      if (forceOverwrite || alcohol === "NIL") {
        if (data.history?.alcohol && data.history.alcohol !== "NIL") {
          setAlcohol(data.history.alcohol);
        }
      }
      if (forceOverwrite || drugAbuse === "NIL") {
        if (data.history?.drugAbuse && data.history.drugAbuse !== "NIL") {
          setDrugAbuse(data.history.drugAbuse);
        }
      }

      // 3. Current Meds & Allergies
      if (forceOverwrite || !currentMeds) {
        if (data.currentMedsText) setCurrentMeds(data.currentMedsText);
      }
      if (forceOverwrite || !allergies || allergies === "None Known") {
        if (data.allergies) setAllergies(data.allergies);
      }

      // 4. Systemic Reviews (ROS)
      if (data.systemicFlags) {
        const sf = data.systemicFlags;
        if (forceOverwrite || (cvsNad && respNad && endoNad)) {
          if (sf.cvsHtn) {
            setCvsHtn(true);
            setCvsHtnStatus(sf.cvsHtnStatus || "WELL_CONTROLLED");
            setCvsNad(false);
          }
          if (sf.cvsIhd) {
            setCvsIhd(true);
            setCvsNad(false);
          }
          if (sf.cvsHf) {
            setCvsHf(true);
            setCvsNad(false);
          }
          if (sf.cvsDyslipidemia) {
            setCvsDyslipidemia(true);
            setCvsNad(false);
          }
          if (sf.cvsPalpitation) {
            setCvsPalpitation(true);
            setCvsNad(false);
          }
          if (sf.respAsthma) {
            setRespAsthma(true);
            setRespNad(false);
          }
          if (sf.respCopd) {
            setRespCopd(true);
            setRespNad(false);
          }
          if (sf.respTb) {
            setRespTb(true);
            setRespNad(false);
          }
          if (sf.respCough) {
            setRespCough(true);
            setRespNad(false);
          }
          if (sf.respDyspnea) {
            setRespDyspnea(true);
            setRespNad(false);
          }
          if (sf.giGerd) {
            setGiGerd(true);
            setGiNad(false);
          }
          if (sf.giJaundice) {
            setGiJaundice(true);
            setGiNad(false);
          }
          if (sf.giCirrhosis) {
            setGiCirrhosis(true);
            setGiNad(false);
          }
          if (sf.renalCrf) {
            setRenalCrf(true);
            setRenalNad(false);
          }
          if (sf.renalDialysis) {
            setRenalDialysis(true);
            setRenalNad(false);
          }
          if (sf.renalEsrd) {
            setRenalEsrd(true);
            setRenalNad(false);
          }
          if (sf.endoDm) {
            setEndoDm(true);
            setEndoDmType(sf.endoDmType || "TYPE_2");
            setEndoNad(false);
          }
          if (sf.endoThyroid && sf.endoThyroid !== "NORMAL") {
            setEndoThyroid(sf.endoThyroid);
            setEndoNad(false);
          }
          if (sf.endoAdrenal) {
            setEndoAdrenal(true);
            setEndoNad(false);
          }
        }
        if (sf.dentures && (forceOverwrite || !dentures)) {
          setDentures(true);
        }
      }

      // 5. Pre-Op Diagnostic Investigations
      if (data.labParams) {
        const lp = data.labParams;
        if (forceOverwrite || !cbcHb) {
          if (lp.cbcDate) setCbcDate(lp.cbcDate);
          if (lp.cbcHb) setCbcHb(lp.cbcHb);
          if (lp.cbcWbc) setCbcWbc(lp.cbcWbc);
          if (lp.cbcHct) setCbcHct(lp.cbcHct);
          if (lp.cbcPlt) setCbcPlt(lp.cbcPlt);
        }
        if (forceOverwrite || !chemCr) {
          if (lp.chemDate) setChemDate(lp.chemDate);
          if (lp.chemNa) setChemNa(lp.chemNa);
          if (lp.chemK) setChemK(lp.chemK);
          if (lp.chemCl) setChemCl(lp.chemCl);
          if (lp.chemHco3) setChemHco3(lp.chemHco3);
          if (lp.chemBun) setChemBun(lp.chemBun);
          if (lp.chemCr) setChemCr(lp.chemCr);
          if (lp.chemGlu) setChemGlu(lp.chemGlu);
        }
        if (forceOverwrite || !coagInr) {
          if (lp.coagDate) setCoagDate(lp.coagDate);
          if (lp.coagPt) setCoagPt(lp.coagPt);
          if (lp.coagPtt) setCoagPtt(lp.coagPtt);
          if (lp.coagAptt) setCoagAptt(lp.coagAptt);
          if (lp.coagInr) setCoagInr(lp.coagInr);
        }
      }
      if (data.cxrFindings && (forceOverwrite || !cxrFindings)) {
        setCxrFindings(data.cxrFindings);
      }
      if (data.ecgFindings && (forceOverwrite || !ecgFindings)) {
        setEcgFindings(data.ecgFindings);
      }

      // 6. Active Referrals
      if (Array.isArray(data.activeReferrals) && data.activeReferrals.length > 0) {
        setActiveReferrals((prev) => {
          const existingIds = new Set(prev.map((r) => String(r.id)));
          const toAdd = data.activeReferrals.filter((r) => !existingIds.has(String(r.id)));
          return [...prev, ...toAdd];
        });
        setConsultRequired(true);
      }

      setAutoPopulatedSummary(data);
    },
    [
      bloodGroup,
      weightKg,
      heightCm,
      pulsePerMin,
      bpSys,
      bpDia,
      rrPerMin,
      spo2Percent,
      tempC,
      fio2Percent,
      neuroMental,
      summaryHistory,
      previousSurgeries.length,
      familyAnesthComp,
      smokingPacks,
      alcohol,
      drugAbuse,
      currentMeds,
      allergies,
      cvsNad,
      respNad,
      endoNad,
      dentures,
      cbcHb,
      chemCr,
      coagInr,
      cxrFindings,
      ecgFindings,
    ]
  );

  const handleManualSync = async () => {
    const admissionId = admissionIdOf(otCase);
    if (!admissionId) return;
    setSyncingClinicalData(true);
    try {
      const data = await fetchClinicalDataForPac(admissionId, otCase);
      if (data) {
        applyAggregatedClinicalData(data, true);
        toast.success("Pre-Anesthesia Assessment synced with latest hospital records!");
      }
    } catch (err) {
      console.error("Failed to sync hospital clinical data:", err);
      toast.error("Failed to sync with hospital clinical records");
    } finally {
      setSyncingClinicalData(false);
    }
  };

  // Pre-load existing PAC assessment or auto-populate from hospital modules
  useEffect(() => {
    if (!otCase?.id) return;
    let cancelled = false;
    const admissionId = admissionIdOf(otCase);

    Promise.allSettled([
      fetchPac(otCase.id, Boolean(otCase.is_request)),
      admissionId ? fetchClinicalDataForPac(admissionId, otCase) : Promise.resolve(null),
    ])
      .then(([pacRes, clinicalRes]) => {
        if (cancelled) return;
        const cur = pacRes.status === "fulfilled" ? pacRes.value?.current : null;
        const clinicalData = clinicalRes.status === "fulfilled" ? clinicalRes.value : null;

        if (cur) {
          if (cur.outcome) setOutcome(cur.outcome);
          if (cur.asa_class) setAsaClass(cur.asa_class);
          if (cur.asa_emergency != null) setAsaEmergency(cur.asa_emergency);
          if (cur.mallampati_score) setMallampati(cur.mallampati_score);
          if (cur.suggested_medications) setSuggestedMeds(cur.suggested_medications);
          if (cur.optimization_instructions) setOptInstructions(cur.optimization_instructions);
          if (cur.next_review_date) setNextReviewDate(cur.next_review_date);
          if (cur.notes) setNotes(cur.notes);
          if (cur.anesthesia_plan) setAnesthesiaPlan(cur.anesthesia_plan);
          if (cur.icu_booking_required != null) setIcuBooking(cur.icu_booking_required);
          if (cur.consent_risk_level) setConsentRiskLevel(cur.consent_risk_level);
          if (cur.reassessment_notes) setReassessmentNotes(cur.reassessment_notes);

          if (cur.vitals) {
            if (cur.vitals.pulse) setPulsePerMin(cur.vitals.pulse);
            if (cur.vitals.bp) {
              const parts = String(cur.vitals.bp).split("/");
              if (parts[0]) setBpSys(parts[0]);
              if (parts[1]) setBpDia(parts[1]);
            }
            if (cur.vitals.spo2) setSpo2Percent(cur.vitals.spo2);
            if (cur.vitals.rr) setRrPerMin(cur.vitals.rr);
            if (cur.vitals.temp) setTempC(cur.vitals.temp);
          }

          if (cur.blood_units_required) {
            if (cur.blood_units_required.prbc) setBloodPrbc(cur.blood_units_required.prbc);
            if (cur.blood_units_required.whole_blood) setBloodWb(cur.blood_units_required.whole_blood);
            if (cur.blood_units_required.ffp) setBloodFfp(cur.blood_units_required.ffp);
            if (cur.blood_units_required.platelets) setBloodPlt(cur.blood_units_required.platelets);
          }

          const d = cur.assessment_data;
          if (d && typeof d === "object") {
            if (d.demographics?.weight_kg) setWeightKg(d.demographics.weight_kg);
            if (d.demographics?.height_cm) setHeightCm(d.demographics.height_cm);
            if (d.demographics?.blood_group) setBloodGroup(d.demographics.blood_group);
            if (d.medical_history?.summary) setSummaryHistory(d.medical_history.summary);
            if (d.medical_history?.previous_surgeries) setPreviousSurgeries(d.medical_history.previous_surgeries);
            if (d.medical_history?.current_medications) setCurrentMeds(d.medical_history.current_medications);
            if (d.medical_history?.family_anesthesia_complications) {
              setFamilyAnesthComp(Boolean(d.medical_history.family_anesthesia_complications.present));
              setFamilyAnesthCompDesc(d.medical_history.family_anesthesia_complications.description || "");
            }
            if (d.medical_history?.habits) {
              if (d.medical_history.habits.allergies) setAllergies(d.medical_history.habits.allergies);
              if (d.medical_history.habits.alcohol) setAlcohol(d.medical_history.habits.alcohol);
              if (d.medical_history.habits.smoking) {
                setSmokingPacks(d.medical_history.habits.smoking.packs_per_day || "");
                setSmokingYears(d.medical_history.habits.smoking.years || "");
                setSmokingStopped(Boolean(d.medical_history.habits.smoking.stopped));
              }
              if (d.medical_history.habits.drug_abuse) setDrugAbuse(d.medical_history.habits.drug_abuse);
            }
            if (d.physical_examination?.airway) {
              if (d.physical_examination.airway.mouth_opening) setMouthOpening(d.physical_examination.airway.mouth_opening);
              if (d.physical_examination.airway.mouth_opening_fingers) setMouthOpeningFingers(d.physical_examination.airway.mouth_opening_fingers);
              if (d.physical_examination.airway.neck_movements) setNeckMovements(d.physical_examination.airway.neck_movements);
              if (d.physical_examination.airway.osa) setOsa(d.physical_examination.airway.osa);
              if (d.physical_examination.airway.thyromental_distance_cm) setThyromentalDistance(d.physical_examination.airway.thyromental_distance_cm);
            }
            if (d.physical_examination?.dental) {
              setDentures(Boolean(d.physical_examination.dental.dentures));
              setPatientInformed(Boolean(d.physical_examination.dental.patient_informed));
              setExpectedDifficultAirway(Boolean(d.physical_examination.dental.expected_difficult_airway));
              const map = {};
              (d.physical_examination.dental.missing_teeth || []).forEach((t) => (map[t] = "MISSING"));
              (d.physical_examination.dental.loose_teeth || []).forEach((t) => (map[t] = "LOOSE"));
              setDentalMarks(map);
            }
            if (d.investigations?.cbc) {
              setCbcDate(d.investigations.cbc.date || "");
              setCbcWbc(d.investigations.cbc.wbc || "");
              setCbcHb(d.investigations.cbc.hb || "");
              setCbcHct(d.investigations.cbc.hct || "");
              setCbcPlt(d.investigations.cbc.platelets || "");
            }
            if (d.investigations?.biochemistry) {
              setChemDate(d.investigations.biochemistry.date || "");
              setChemNa(d.investigations.biochemistry.na || "");
              setChemK(d.investigations.biochemistry.k || "");
              setChemCl(d.investigations.biochemistry.cl || "");
              setChemHco3(d.investigations.biochemistry.hco3 || "");
              setChemBun(d.investigations.biochemistry.bun || "");
              setChemCr(d.investigations.biochemistry.cr || "");
              setChemGlu(d.investigations.biochemistry.glucose || "");
            }
            if (d.investigations?.coagulation) {
              setCoagDate(d.investigations.coagulation.date || "");
              setCoagPt(d.investigations.coagulation.pt || "");
              setCoagPtt(d.investigations.coagulation.ptt || "");
              setCoagAptt(d.investigations.coagulation.aptt || "");
              setCoagInr(d.investigations.coagulation.inr || "");
            }
            if (d.investigations?.cxr) setCxrFindings(d.investigations.cxr);
            if (d.investigations?.ecg) setEcgFindings(d.investigations.ecg);
            if (d.investigations?.other_investigations) setOtherInvestigations(d.investigations.other_investigations);
            if (d.investigations?.other_labs) setOtherLabs(d.investigations.other_labs);
            if (d.demographics?.procedure_date) setProcedureDate(d.demographics.procedure_date.substring(0, 10));
            if (d.systemic_reviews?.neuro?.others) setNeuroOthers(d.systemic_reviews.neuro.others);
            if (d.systemic_reviews?.cardiovascular) {
              const cv = d.systemic_reviews.cardiovascular;
              if (cv.arrhythmias) setCvsArrhythmias(cv.arrhythmias);
              if (cv.heart_failure) {
                setCvsHf(Boolean(cv.heart_failure.present));
                setCvsHfNyha(cv.heart_failure.nyha_class || "I");
              }
              if (cv.lvef_percent) setCvsLvef(String(cv.lvef_percent));
              if (cv.valvular_disease) setCvsValvular(cv.valvular_disease);
              if (cv.others) setCvsOthers(cv.others);
              if (cv.auscultation_heart) setCvsAuscHeart(cv.auscultation_heart);
              if (cv.auscultation_lungs) setCvsAuscLungs(cv.auscultation_lungs);
            }
            if (d.systemic_reviews?.respiratory?.others) setRespOthers(d.systemic_reviews.respiratory.others);
            if (d.systemic_reviews?.gastrointestinal?.others) setGiOthers(d.systemic_reviews.gastrointestinal.others);
            if (d.systemic_reviews?.renal) {
              if (d.systemic_reviews.renal.crf?.ccr_ml_min) setRenalCcr(String(d.systemic_reviews.renal.crf.ccr_ml_min));
              if (d.systemic_reviews.renal.others) setRenalOthers(d.systemic_reviews.renal.others);
            }
            if (cur.vitals?.fio2) setFio2Percent(String(cur.vitals.fio2));
            if (d.physical_examination?.vitals?.physical_notes) setPhysicalNotes(d.physical_examination.vitals.physical_notes);
            if (d.physical_examination?.vitals?.fasting_hours) setFastingHours(String(d.physical_examination.vitals.fasting_hours));
            if (d.physical_examination?.neck?.mass) {
              setNeckMass(Boolean(d.physical_examination.neck.mass.present));
              setNeckMassDesc(d.physical_examination.neck.mass.description || "");
            }
            if (d.systemic_reviews?.neuro) {
              if (d.systemic_reviews.neuro.mental_status) setNeuroMental(d.systemic_reviews.neuro.mental_status);
              if (d.systemic_reviews.neuro.syncope != null) setNeuroSyncope(Boolean(d.systemic_reviews.neuro.syncope));
              if (d.systemic_reviews.neuro.weakness != null) setNeuroWeakness(Boolean(d.systemic_reviews.neuro.weakness));
              if (d.systemic_reviews.neuro.myopathies != null) setNeuroMyopathies(Boolean(d.systemic_reviews.neuro.myopathies));
              if (d.systemic_reviews.neuro.chronic_pain != null) setNeuroChronicPain(Boolean(d.systemic_reviews.neuro.chronic_pain));
            }
            if (d.systemic_reviews?.cardiovascular) {
              if (d.systemic_reviews.cardiovascular.hypertension?.status) setCvsHtnStatus(d.systemic_reviews.cardiovascular.hypertension.status);
              if (d.systemic_reviews.cardiovascular.dyslipidemia != null) setCvsDyslipidemia(Boolean(d.systemic_reviews.cardiovascular.dyslipidemia));
              if (d.systemic_reviews.cardiovascular.palpitation != null) setCvsPalpitation(Boolean(d.systemic_reviews.cardiovascular.palpitation));
            }
            if (d.systemic_reviews?.respiratory) {
              if (d.systemic_reviews.respiratory.cough != null) setRespCough(Boolean(d.systemic_reviews.respiratory.cough));
              if (d.systemic_reviews.respiratory.dyspnea != null) setRespDyspnea(Boolean(d.systemic_reviews.respiratory.dyspnea));
            }
            if (d.systemic_reviews?.gastrointestinal) {
              if (d.systemic_reviews.gastrointestinal.cirrhosis != null) setGiCirrhosis(Boolean(d.systemic_reviews.gastrointestinal.cirrhosis));
            }
            if (d.systemic_reviews?.renal) {
              if (d.systemic_reviews.renal.esrd != null) setRenalEsrd(Boolean(d.systemic_reviews.renal.esrd));
            }
            if (d.systemic_reviews?.endocrine) {
              if (d.systemic_reviews.endocrine.diabetes?.type) setEndoDmType(d.systemic_reviews.endocrine.diabetes.type);
              if (d.systemic_reviews.endocrine.adrenal != null) setEndoAdrenal(Boolean(d.systemic_reviews.endocrine.adrenal));
            }
            if (d.systemic_reviews?.endocrine) {
              if (d.systemic_reviews.endocrine.diabetes?.hba1c_percent) setEndoHba1c(String(d.systemic_reviews.endocrine.diabetes.hba1c_percent));
              if (d.systemic_reviews.endocrine.lmp) setEndoLmp(d.systemic_reviews.endocrine.lmp);
              if (d.systemic_reviews.endocrine.others) setEndoOthers(d.systemic_reviews.endocrine.others);
            }
            if (d.investigations?.consultations) {
              setConsultRequired(Boolean(d.investigations.consultations.required));
              setConsultSpecialty(d.investigations.consultations.specialty || "");
              setConsultAccepted(Boolean(d.investigations.consultations.accepted));
              setConsultReason(d.investigations.consultations.reason || "");
            }
            if (d.plan_and_logistics?.risk_discussion) {
              setRiskDiscussedWith(d.plan_and_logistics.risk_discussion.discussed_with || "PATIENT");
              setAgreedToPlan(Boolean(d.plan_and_logistics.risk_discussion.agreed_to_plan));
              setKinNameRelation(d.plan_and_logistics.risk_discussion.name_and_relationship || "");
            }
            if (d.plan_and_logistics?.consent?.risk_details) setConsentRiskDetails(d.plan_and_logistics.consent.risk_details);
            if (d.plan_and_logistics?.blood_arrangements?.remarks) setBloodRemarks(d.plan_and_logistics.blood_arrangements.remarks);
          }

          // Fallback: fill any unrecorded fields from connected hospital modules
          if (clinicalData) {
            applyAggregatedClinicalData(clinicalData, false);
          }
        } else {
          // New PAC -> pre-populate all available fields from connected hospital modules
          if (clinicalData) {
            applyAggregatedClinicalData(clinicalData, true);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load PAC or clinical records:", err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [otCase?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tooth mark cycling handler: NORMAL -> MISSING -> LOOSE -> NORMAL
  const handleToothClick = (toothKey) => {
    setDentalMarks((prev) => {
      const current = prev[toothKey] || "NORMAL";
      const next = getNextToothState(current);
      if (next === "NORMAL") {
        const copy = { ...prev };
        delete copy[toothKey];
        return copy;
      }
      return { ...prev, [toothKey]: next };
    });
  };

  const handleAddPreviousSurgery = () => {
    setPreviousSurgeries((prev) => [
      ...prev,
      {
        id: Date.now(),
        surgery_name: "",
        year: "",
        anesthesia_type: "General Anesthesia",
        complications: "None",
      },
    ]);
  };

  const handleRemovePreviousSurgery = (id) => {
    setPreviousSurgeries((prev) => prev.filter((s) => s.id !== id));
  };

  const handleUpdatePreviousSurgery = (id, field, value) => {
    setPreviousSurgeries((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  const buildPayload = () => {
    const missingTeeth = Object.entries(dentalMarks)
      .filter(([, val]) => val === "MISSING")
      .map(([k]) => k);
    const looseTeeth = Object.entries(dentalMarks)
      .filter(([, val]) => val === "LOOSE")
      .map(([k]) => k);

    const assessmentData = {
      demographics: {
        weight_kg: weightKg ? Number(weightKg) : undefined,
        height_cm: heightCm ? Number(heightCm) : undefined,
        bmi: computedBmi ? Number(computedBmi) : undefined,
        diagnosis,
        procedure_planned: procedurePlanned,
        procedure_date: procedureDate,
        department,
        blood_group: bloodGroup,
      },
      medical_history: {
        summary: summaryHistory,
        previous_surgeries: previousSurgeries,
        current_medications: currentMeds,
        family_anesthesia_complications: {
          present: familyAnesthComp,
          description: familyAnesthComp ? familyAnesthCompDesc : "",
        },
        habits: {
          allergies,
          alcohol,
          smoking: {
            packs_per_day: smokingPacks ? Number(smokingPacks) : undefined,
            years: smokingYears ? Number(smokingYears) : undefined,
            stopped: smokingStopped,
          },
          drug_abuse: drugAbuse,
        },
      },
      physical_examination: {
        vitals: {
          bp_systolic: bpSys ? Number(bpSys) : undefined,
          bp_diastolic: bpDia ? Number(bpDia) : undefined,
          pulse_per_min: pulsePerMin ? Number(pulsePerMin) : undefined,
          temperature_c: tempC ? Number(tempC) : undefined,
          respiratory_rate: rrPerMin ? Number(rrPerMin) : undefined,
          spo2_percent: spo2Percent ? Number(spo2Percent) : undefined,
          fio2_percent: fio2Percent ? Number(fio2Percent) : undefined,
          pallor,
          physical_notes: physicalNotes,
          fasting_hours: fastingHours ? Number(fastingHours) : undefined,
        },
        airway: {
          mouth_opening: mouthOpening,
          mouth_opening_fingers: mouthOpeningFingers ? Number(mouthOpeningFingers) : undefined,
          neck_movements: neckMovements,
          osa,
          mallampati,
          thyromental_distance_cm: thyromentalDistance ? Number(thyromentalDistance) : undefined,
          neck_mass: {
            present: neckMass,
            description: neckMass ? neckMassDesc : "",
          },
        },
        dental: {
          dentures,
          missing_teeth: missingTeeth,
          loose_teeth: looseTeeth,
          patient_informed: patientInformed,
          expected_difficult_airway: expectedDifficultAirway,
        },
      },
      systemic_reviews: {
        neuro: {
          mental_status: neuroMental,
          cva: neuroCva,
          seizures: neuroSeizures,
          syncope: neuroSyncope,
          weakness: neuroWeakness,
          myopathies: neuroMyopathies,
          chronic_pain: neuroChronicPain,
          others: neuroOthers,
        },
        cardiovascular: {
          nad: cvsNad,
          hypertension: {
            present: cvsHtn,
            status: cvsHtn ? cvsHtnStatus : "NONE",
          },
          ihd: cvsIhd,
          arrhythmias: cvsArrhythmias,
          heart_failure: {
            present: cvsHf,
            nyha_class: cvsHf ? cvsHfNyha : "NONE",
          },
          dyslipidemia: cvsDyslipidemia,
          palpitations: cvsPalpitation,
          lvef_percent: cvsLvef ? Number(cvsLvef) : undefined,
          valvular_disease: cvsValvular,
          auscultation_heart: cvsAuscHeart,
          auscultation_lungs: cvsAuscLungs,
          others: cvsOthers,
        },
        respiratory: {
          nad: respNad,
          copd: respCopd,
          asthma: respAsthma,
          recent_uri: respRecentUri,
          tb: respTb,
          cough: respCough,
          dyspnea: respDyspnea,
          others: respOthers,
        },
        gastrointestinal: {
          nad: giNad,
          gerd: giGerd,
          jaundice: giJaundice,
          cirrhosis: giCirrhosis,
          others: giOthers,
        },
        renal: {
          nad: renalNad,
          crf: {
            present: renalCrf,
            ccr_ml_min: renalCcr ? Number(renalCcr) : undefined,
          },
          esrd: renalEsrd,
          dialysis: renalDialysis,
          others: renalOthers,
        },
        endocrine: {
          nad: endoNad,
          diabetes: {
            present: endoDm,
            type: endoDm ? endoDmType : "NONE",
            hba1c_percent: endoHba1c ? Number(endoHba1c) : undefined,
          },
          thyroid: endoThyroid,
          adrenal: endoAdrenal,
          lmp: endoLmp,
          others: endoOthers,
        },
      },
      investigations: {
        cbc: { date: cbcDate, wbc: cbcWbc, hb: cbcHb, hct: cbcHct, platelets: cbcPlt },
        biochemistry: {
          date: chemDate,
          na: chemNa,
          k: chemK,
          cl: chemCl,
          hco3: chemHco3,
          bun: chemBun,
          cr: chemCr,
          glucose: chemGlu,
        },
        coagulation: { date: coagDate, pt: coagPt, ptt: coagPtt, aptt: coagAptt, inr: coagInr },
        cxr: cxrFindings,
        ecg: ecgFindings,
        other_investigations: otherInvestigations,
        other_labs: otherLabs,
        cpoe_orders_summary: cpoeOrders.slice(0, 15).map((o) => ({
          order_no: o.order_no,
          service: o.service_name,
          status: o.status,
          type: o.type,
        })),
        consultations: {
          required: consultRequired,
          specialty: consultSpecialty,
          accepted: consultAccepted,
          reason: consultReason,
        },
      },
      plan_and_logistics: {
        anesthesia_plan: anesthesiaPlan,
        risk_discussion: {
          discussed_with: riskDiscussedWith,
          agreed_to_plan: agreedToPlan,
          name_and_relationship: kinNameRelation,
        },
        consent: {
          risk_level: consentRiskLevel,
          risk_details: consentRiskDetails,
        },
        blood_arrangements: {
          prbc: bloodPrbc,
          whole_blood: bloodWb,
          ffp: bloodFfp,
          platelets: bloodPlt,
          remarks: bloodRemarks,
        },
        icu_hdu_booking: icuBooking,
        preop_advice_and_premeds: suggestedMeds,
      },
    };

    return {
      outcome,
      asa_class: asaClass,
      asa_emergency: Boolean(asaEmergency),
      mallampati_score: mallampati,
      airway_notes: `Mouth Opening: ${mouthOpening} (${mouthOpeningFingers}f), Neck: ${neckMovements}, OSA: ${osa}, Thyromental: ${thyromentalDistance}cm. Difficult Airway: ${expectedDifficultAirway ? "YES" : "NO"}`,
      vitals: {
        pulse: pulsePerMin ? String(pulsePerMin) : undefined,
        bp: bpSys && bpDia ? `${bpSys}/${bpDia}` : undefined,
        spo2: spo2Percent ? String(spo2Percent) : undefined,
        rr: rrPerMin ? String(rrPerMin) : undefined,
        temp: tempC ? String(tempC) : undefined,
      },
      assessment_data: assessmentData,
      anesthesia_plan: anesthesiaPlan,
      blood_units_required: {
        prbc: bloodPrbc || 0,
        whole_blood: bloodWb || 0,
        ffp: bloodFfp || 0,
        platelets: bloodPlt || 0,
      },
      icu_booking_required: Boolean(icuBooking),
      consent_risk_level: consentRiskLevel,
      reassessment_notes: reassessmentNotes,
      // A review date belongs to a deferral; a FIT clearance carries none.
      next_review_date: outcome !== "FIT" ? nextReviewDate || undefined : undefined,
      suggested_medications: suggestedMeds.trim() || undefined,
      optimization_instructions: optInstructions.trim() || undefined,
      notes: notes.trim() || `Clearance recorded as ${outcome}. ${anesthesiaPlan}.`,
    };
  };

  const handleSave = async () => {
    if (outcome !== "FIT" && !notes.trim()) {
      toast.error("Clinical notes / Reason for deferral are required when postponing or optimizing");
      return;
    }
    if (outcome !== "FIT" && !nextReviewDate) {
      toast.error("Please set a next review date for re-evaluation");
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await recordPac(otCase.id, payload, Boolean(otCase.is_request));
      if (res?.ok || res?.status === "Success") {
        toast.success(
          outcome === "FIT"
            ? "Pre-Anesthesia Assessment complete: Patient CLEARED for surgery!"
            : `Assessment saved: ${outcome} (Case Postponed for Optimization)`
        );
        if (onSaved) onSaved();
        if (onClose) onClose();
      } else {
        toast.error(res?.message || "Failed to record PAC assessment");
      }
    } catch (err) {
      toast.error(err.message || "Failed to record PAC assessment");
    } finally {
      setSaving(false);
    }
  };


  const labCount = useMemo(() => cpoeOrders.filter((o) => o.type === "Laboratory").length, [cpoeOrders]);
  const radCount = useMemo(() => cpoeOrders.filter((o) => o.type === "Radiology").length, [cpoeOrders]);

  // Where the patient is, as the server reads it live from the admission.
  // Never a default: an invented bed on a clinical sheet sends people to the wrong bed.
  const wardName = otCase?.location?.ward_name || otCase?.ward_name || "—";
  const bedLabel = otCase?.location?.bed_label || otCase?.bed_number || "—";
  const facilityIdentity = resolveFacilityIdentity();
  const facilityDetails = resolveFacilityDetails();
  const facilityLogo = resolveLogo();

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: "#f8fafc",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Top Header Bar */}
        <DialogTitle
          sx={{
            m: 0,
            py: 1.5,
            px: 3,
            bgcolor: "#0f172a",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #1e293b",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <HealthAndSafetyIcon sx={{ color: "#38bdf8", fontSize: 28 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2, color: "#fff" }}>
                PRE-ANESTHESIA ASSESSMENT & CLEARANCE WORKBENCH
              </Typography>
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                NABH COP.13 Statutory Clinical Evaluation, Live CPOE Diagnostic Orders & Pre-Op Fitness
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<UnfoldMoreIcon />}
              onClick={handleExpandAllGlobal}
              sx={{ color: "#cbd5e1", borderColor: "#475569", textTransform: "none", fontSize: "0.75rem", "&:hover": { bgcolor: "#1e293b" } }}
            >
              Expand All
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UnfoldLessIcon />}
              onClick={handleCollapseAllGlobal}
              sx={{ color: "#cbd5e1", borderColor: "#475569", textTransform: "none", fontSize: "0.75rem", "&:hover": { bgcolor: "#1e293b" } }}
            >
              Collapse All
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SyncIcon />}
              disabled={syncingClinicalData}
              onClick={handleManualSync}
              sx={{ color: "#38bdf8", borderColor: "#0284c7", textTransform: "none", fontSize: "0.75rem", "&:hover": { bgcolor: "#1e293b" } }}
            >
              {syncingClinicalData ? "Syncing..." : "Sync Hospital Data"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => {
                const pacPayload = buildPayload();
                printAnesthesiaConsentForm(otCase, pacPayload);
              }}
              sx={{ color: "#38bdf8", borderColor: "#38bdf8", textTransform: "none", fontSize: "0.75rem", "&:hover": { bgcolor: "#1e293b" } }}
            >
              Print Consent (NABH COP.13)
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => {
                const pacPayload = buildPayload();
                printPreAnesthesiaAssessmentForm(otCase, pacPayload);
              }}
              sx={{ color: "#a5b4fc", borderColor: "#6366f1", textTransform: "none", fontSize: "0.75rem", "&:hover": { bgcolor: "#1e293b" } }}
            >
              Export Assessment PDF
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => setShowPrintView(true)}
              sx={{ color: "#f8fafc", borderColor: "#64748b", textTransform: "none", fontSize: "0.75rem", "&:hover": { bgcolor: "#1e293b" } }}
            >
              Print Assessment Sheet
            </Button>
            <IconButton size="small" onClick={onClose} sx={{ color: "#94a3b8", "&:hover": { color: "#fff" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        {/* 2-Stage Stepper Header */}
        <Paper square elevation={0} sx={{ bgcolor: "#f1f5f9", px: 3, py: 1.5, borderBottom: "1px solid #cbd5e1" }}>
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            <Button
              variant={activeStage === 1 ? "contained" : "outlined"}
              color={activeStage === 1 ? "primary" : "inherit"}
              onClick={() => setActiveStage(1)}
              startIcon={<AssignmentIcon />}
              sx={{
                fontWeight: 700,
                borderRadius: 2,
                textTransform: "none",
                bgcolor: activeStage === 1 ? "#0284c7" : "#fff",
                color: activeStage === 1 ? "#fff" : "#475569",
              }}
            >
              Step 1: Comprehensive Pre-Anesthesia Assessment (5 Subsections)
            </Button>
            <Typography variant="body2" sx={{ color: "#94a3b8", fontWeight: 700 }}>
              ➔
            </Typography>
            <Button
              variant={activeStage === 2 ? "contained" : "outlined"}
              color={activeStage === 2 ? "primary" : "inherit"}
              onClick={() => setActiveStage(2)}
              startIcon={<FitnessCenterIcon />}
              sx={{
                fontWeight: 700,
                borderRadius: 2,
                textTransform: "none",
                bgcolor: activeStage === 2 ? "#0f766e" : "#fff",
                color: activeStage === 2 ? "#fff" : "#475569",
              }}
            >
              Step 2: Fitness Outcome & Pre-Op Orders
            </Button>
          </Stack>
        </Paper>

        <DialogContent dividers sx={{ p: 3, bgcolor: "#f8fafc", flex: 1, overflowY: "auto" }}>
          {/* Patient Header Card */}
          <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 2, border: "1px solid #e2e8f0", bgcolor: "#fff" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Patient Name & UHID</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  {otCase.patient_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {otCase.health_record_id || otCase.patient_id} · {otCase.gender} {otCase.age ? `${otCase.age}y` : ""}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Ward / Bed / Department</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  {wardName} / {bedLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Dept: {department || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Planned Procedure & Surgeon</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  {procedurePlanned || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Surgeon: {otCase.primary_surgeon_name || otCase.surgeon_name || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">Anthropometrics & BMI</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                    {weightKg || "—"} kg / {heightCm || "—"} cm
                  </Typography>
                  {computedBmi && (
                    <Chip
                      size="small"
                      label={`BMI ${computedBmi}`}
                      color={getBmiCategory(computedBmi).color}
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Clinical Records Pre-Population Notification Banner */}
          {autoPopulatedSummary && (
            <Paper
              elevation={0}
              sx={{
                mb: 2.5,
                p: 1.75,
                bgcolor: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AutoAwesomeIcon sx={{ color: "#16a34a", fontSize: 24 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#166534" }}>
                    Clinical Data Pre-Populated from Connected Hospital Systems
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#15803d" }}>
                    Baseline vitals, clinical history, active medications, allergies, and diagnostic lab values were aggregated from IPD Admission, Nursing, Doctor Notes, and CPOE LIS/RIS. You can review and adjust any field.
                  </Typography>
                </Box>
              </Stack>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SyncIcon />}
                disabled={syncingClinicalData}
                onClick={handleManualSync}
                sx={{
                  bgcolor: "#fff",
                  borderColor: "#86efac",
                  color: "#166534",
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { bgcolor: "#f0fdf4" },
                }}
              >
                {syncingClinicalData ? "Syncing..." : "Re-Sync Hospital Data"}
              </Button>
            </Paper>
          )}

          {/* ========================================================================= */}
          {/* STAGE 1: 5 SUBSECTIONS (ACCORDION ARCHITECTURE)                           */}
          {/* ========================================================================= */}
          {activeStage === 1 && (
            <Box>
              {/* 5-Tab Navigation Bar */}
              <Tabs
                value={formSubTab}
                onChange={(_, v) => setFormSubTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  mb: 2.5,
                  borderBottom: "1px solid #cbd5e1",
                  "& .MuiTab-root": { textTransform: "none", fontSize: "0.88rem", fontWeight: 700, minHeight: 44 },
                }}
              >
                <Tab label="1. Demographics & Vitals" />
                <Tab label="2. History & Habits" />
                <Tab label="3. Airway, Dental & Systemic" />
                <Tab
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>4. Lab & Radiology Orders (CPOE)</span>
                      {cpoeOrders.length > 0 && (
                        <Chip
                          size="small"
                          label={cpoeOrders.length}
                          sx={{ height: 18, fontSize: "0.7rem", fontWeight: 800, bgcolor: "#e0f2fe", color: "#0369a1" }}
                        />
                      )}
                    </Stack>
                  }
                />
                <Tab label="5. Anesthesia Plan & Logistics" />
              </Tabs>

              {/* Toolbar with Section Title & Expand/Collapse All */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "0.95rem" }}>
                  {formSubTab === 0 && "Subsection 1: Demographics, Anthropometrics & Baseline Vital Signs"}
                  {formSubTab === 1 && "Subsection 2: Medical, Surgical & Family History, Habits & Allergies"}
                  {formSubTab === 2 && "Subsection 3: Airway Assessment, 32-Tooth Dental Chart & Systemic Reviews"}
                  {formSubTab === 3 && "Subsection 4: Computerized Physician Order Entry (CPOE) Orders & Diagnostic Results"}
                  {formSubTab === 4 && "Subsection 5: Anesthesia Plan, Cross-Specialty Consultation & Perioperative Logistics"}
                </Typography>
                {formSubTab !== 3 && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UnfoldMoreIcon sx={{ fontSize: 16 }} />}
                      onClick={() => handleExpandAll(formSubTab)}
                      sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 700, borderRadius: 1.5 }}
                    >
                      Expand All
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UnfoldLessIcon sx={{ fontSize: 16 }} />}
                      onClick={() => handleCollapseAll(formSubTab)}
                      sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 700, borderRadius: 1.5 }}
                    >
                      Collapse All
                    </Button>
                  </Stack>
                )}
              </Stack>

              {/* --------------------------------------------------------------------- */}
              {/* SUBSECTION 1: DEMOGRAPHICS & VITALS                                  */}
              {/* --------------------------------------------------------------------- */}
              {formSubTab === 0 && (
                <Stack spacing={2}>
                  {/* Accordion 1.1: Physical Metrics */}
                  <Accordion
                    expanded={Boolean(expandedSec0[0])}
                    onChange={() => setExpandedSec0((prev) => ({ ...prev, 0: !prev[0] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <LocalHospitalIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          1.1 Physical Metrics & Anthropometrics
                        </Typography>
                        <Chip size="small" label={`${weightKg || "--"} kg · ${heightCm || "--"} cm · BMI ${computedBmi || "--"}`} sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Body Weight (kg)"
                            value={weightKg}
                            onChange={(e) => setWeightKg(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Height (cm)"
                            value={heightCm}
                            onChange={(e) => setHeightCm(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Calculated BMI"
                            value={computedBmi ? `${computedBmi} kg/m²` : "—"}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="BMI Category"
                            value={computedBmi ? getBmiCategory(computedBmi).label : "—"}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Pre-Op Diagnosis"
                            value={diagnosis}
                            onChange={(e) => setDiagnosis(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Planned Surgical Procedure"
                            value={procedurePlanned}
                            onChange={(e) => setProcedurePlanned(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Procedure Date"
                            InputLabelProps={{ shrink: true }}
                            value={procedureDate}
                            onChange={(e) => setProcedureDate(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Surgical Department"
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Blood Group & Rh"
                            value={bloodGroup}
                            onChange={(e) => setBloodGroup(e.target.value)}
                          >
                            <MenuItem value=""><em>Unspecified</em></MenuItem>
                            <MenuItem value="A+">A Positive (A+)</MenuItem>
                            <MenuItem value="A-">A Negative (A-)</MenuItem>
                            <MenuItem value="B+">B Positive (B+)</MenuItem>
                            <MenuItem value="B-">B Negative (B-)</MenuItem>
                            <MenuItem value="O+">O Positive (O+)</MenuItem>
                            <MenuItem value="O-">O Negative (O-)</MenuItem>
                            <MenuItem value="AB+">AB Positive (AB+)</MenuItem>
                            <MenuItem value="AB-">AB Negative (AB-)</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Accordion 1.2: Baseline Vitals */}
                  <Accordion
                    expanded={Boolean(expandedSec0[1])}
                    onChange={() => setExpandedSec0((prev) => ({ ...prev, 1: !prev[1] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <HealthAndSafetyIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          1.2 Baseline Vital Signs & Physical Inspection
                        </Typography>
                        <Chip size="small" label={`BP ${bpSys || "--"}/${bpDia || "--"} · Pulse ${pulsePerMin || "--"} · SpO2 ${spo2Percent || "--"}%`} sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Pulse Rate (bpm)"
                            value={pulsePerMin}
                            onChange={(e) => setPulsePerMin(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Stack direction="row" spacing={1}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="BP Systolic"
                              value={bpSys}
                              onChange={(e) => setBpSys(e.target.value)}
                              placeholder="120"
                            />
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="BP Diastolic"
                              value={bpDia}
                              onChange={(e) => setBpDia(e.target.value)}
                              placeholder="80"
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Respiratory Rate (/min)"
                            value={rrPerMin}
                            onChange={(e) => setRrPerMin(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="SpO2 (%)"
                            value={spo2Percent}
                            onChange={(e) => setSpo2Percent(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="FiO2 (%)"
                            placeholder="21"
                            value={fio2Percent}
                            onChange={(e) => setFio2Percent(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Temperature (°F/°C)"
                            value={tempC}
                            onChange={(e) => setTempC(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="General Pallor"
                            value={pallor}
                            onChange={(e) => setPallor(e.target.value)}
                          >
                            <MenuItem value="ABSENT">Absent (Normal Pink)</MenuItem>
                            <MenuItem value="MILD">Mild Pallor</MenuItem>
                            <MenuItem value="MODERATE">Moderate Pallor</MenuItem>
                            <MenuItem value="SEVERE">Severe Pallor</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Fasting / NPO (Hours)"
                            value={fastingHours}
                            onChange={(e) => setFastingHours(e.target.value)}
                            placeholder="e.g. 6"
                          />
                        </Grid>
                        <Grid item xs={12} sm={7}>
                          <TextField
                            fullWidth
                            size="small"
                            label="General Physical Inspection Notes"
                            value={physicalNotes}
                            onChange={(e) => setPhysicalNotes(e.target.value)}
                            placeholder="Icterus, cyanosis, clubbing, lymphadenopathy, peripheral edema, hydration status..."
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1 }}>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => setFormSubTab(1)}
                      sx={{ bgcolor: "#0284c7", textTransform: "none", fontWeight: 700 }}
                    >
                      Next: Medical History & Habits
                    </Button>
                  </Stack>
                </Stack>
              )}

              {/* --------------------------------------------------------------------- */}
              {/* SUBSECTION 2: MEDICAL HISTORY & HABITS                                */}
              {/* --------------------------------------------------------------------- */}
              {formSubTab === 1 && (
                <Stack spacing={2}>
                  {/* Accordion 2.1: Medical History & Surgeries */}
                  <Accordion
                    expanded={Boolean(expandedSec1[0])}
                    onChange={() => setExpandedSec1((prev) => ({ ...prev, 0: !prev[0] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <AssignmentIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          2.1 Medical History & Previous Surgical Procedures
                        </Typography>
                        <Chip size="small" label={`${previousSurgeries.length} Past Surgeries`} sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Summary of Medical History"
                        value={summaryHistory}
                        onChange={(e) => setSummaryHistory(e.target.value)}
                        placeholder="Document major past medical illnesses, prior hospitalizations, chronic diseases..."
                        sx={{ mb: 2 }}
                      />

                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 1 }}>
                        Previous Surgical Interventions & Anesthetic History
                      </Typography>
                      {previousSurgeries.map((s) => (
                        <Grid container spacing={1.5} key={s.id} sx={{ mb: 1.5, alignItems: "center" }}>
                          <Grid item xs={12} sm={3.5}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Surgery Name"
                              value={s.surgery_name}
                              onChange={(e) => handleUpdatePreviousSurgery(s.id, "surgery_name", e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Year"
                              value={s.year}
                              onChange={(e) => handleUpdatePreviousSurgery(s.id, "year", e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Anesthesia Type"
                              value={s.anesthesia_type}
                              onChange={(e) => handleUpdatePreviousSurgery(s.id, "anesthesia_type", e.target.value)}
                            >
                              <MenuItem value="General Anesthesia">General Anesthesia</MenuItem>
                              <MenuItem value="Spinal Anesthesia">Spinal Anesthesia</MenuItem>
                              <MenuItem value="Epidural Anesthesia">Epidural Anesthesia</MenuItem>
                              <MenuItem value="Regional Block">Regional Block</MenuItem>
                              <MenuItem value="Local with Sedation">Local with Sedation</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid item xs={10} sm={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Complications"
                              value={s.complications}
                              onChange={(e) => handleUpdatePreviousSurgery(s.id, "complications", e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={2} sm={0.5}>
                            <IconButton size="small" color="error" onClick={() => handleRemovePreviousSurgery(s.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddPreviousSurgery}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Add Previous Surgery Record
                      </Button>
                    </AccordionDetails>
                  </Accordion>

                  {/* Accordion 2.2: Current Meds & Allergies */}
                  <Accordion
                    expanded={Boolean(expandedSec1[1])}
                    onChange={() => setExpandedSec1((prev) => ({ ...prev, 1: !prev[1] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <MedicationIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          2.2 Current Medications & Known Allergies
                        </Typography>
                        {allergies && <Chip size="small" label="Allergies Documented" color="warning" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Current Medications & Dosages"
                            value={currentMeds}
                            onChange={(e) => setCurrentMeds(e.target.value)}
                            placeholder="e.g. Tab Telmisartan 40mg OD, Tab Metformin 500mg BD..."
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Known Drug Allergies & Adverse Reactions"
                            value={allergies}
                            onChange={(e) => setAllergies(e.target.value)}
                            placeholder="e.g. Penicillin (Anaphylaxis), NSAIDs (Bronchospasm), Sulfa..."
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Paper elevation={0} sx={{ p: 1.5, bgcolor: "#fffbeb", border: "1px solid #fef3c7", borderRadius: 1.5 }}>
                            <Typography variant="caption" sx={{ color: "#92400e", fontWeight: 700, display: "block", mb: 0.5 }}>
                              Anticoagulant & Antiplatelet Management Reminder
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#78350f" }}>
                              Verify stop dates for active blood thinners: Aspirin (continue/stop per surgical bleeding risk), Clopidogrel (stop 5-7 days), Warfarin (target INR &lt; 1.5), DOACs (stop 24-48h prior to neuraxial anesthesia).
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Accordion 2.3: Family Anesthesia History */}
                  <Accordion
                    expanded={Boolean(expandedSec1[2])}
                    onChange={() => setExpandedSec1((prev) => ({ ...prev, 2: !prev[2] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <HealthAndSafetyIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          2.3 Family Anesthesia History & Complications
                        </Typography>
                        <Chip size="small" label={familyAnesthComp ? "Complications Reported" : "NAD"} color={familyAnesthComp ? "error" : "default"} sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={familyAnesthComp}
                            onChange={(e) => setFamilyAnesthComp(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Family History of Anesthetic Complications (e.g., Malignant Hyperthermia, Pseudocholinesterase Deficiency, Prolonged Paralysis)
                          </Typography>
                        }
                      />
                      {familyAnesthComp && (
                        <TextField
                          fullWidth
                          size="small"
                          label="Describe Family Complications / Adverse Events"
                          value={familyAnesthCompDesc}
                          onChange={(e) => setFamilyAnesthCompDesc(e.target.value)}
                          placeholder="Provide details regarding affected blood relatives, unexplained perioperative deaths, or hyperthermia..."
                          sx={{ mt: 1.5 }}
                        />
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {/* Accordion 2.4: Habits & Social History */}
                  <Accordion
                    expanded={Boolean(expandedSec1[3])}
                    onChange={() => setExpandedSec1((prev) => ({ ...prev, 3: !prev[3] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <AssignmentIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          2.4 Personal Habits & Social History
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Alcohol Intake"
                            value={alcohol}
                            onChange={(e) => setAlcohol(e.target.value)}
                          >
                            <MenuItem value="NONE">None / Non-Drinker</MenuItem>
                            <MenuItem value="OCCASIONAL">Occasional / Social</MenuItem>
                            <MenuItem value="MODERATE">Moderate Regular</MenuItem>
                            <MenuItem value="HEAVY">Heavy / Daily Intake</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Stack direction="row" spacing={1}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Smoking (Packs/Day)"
                              value={smokingPacks}
                              onChange={(e) => setSmokingPacks(e.target.value)}
                              placeholder="e.g. 1"
                            />
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Smoking (Years)"
                              value={smokingYears}
                              onChange={(e) => setSmokingYears(e.target.value)}
                              placeholder="e.g. 10"
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={smokingStopped}
                                onChange={(e) => setSmokingStopped(e.target.checked)}
                              />
                            }
                            label="Smoking Stopped &gt; 4-8 Weeks"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Substance Use / Other Recreational Drugs"
                            value={drugAbuse}
                            onChange={(e) => setDrugAbuse(e.target.value)}
                            placeholder="Cannabis, Opioids, Sedatives, Chewing Tobacco..."
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
                    <Button variant="outlined" onClick={() => setFormSubTab(0)} sx={{ textTransform: "none" }}>
                      Previous: Demographics & Vitals
                    </Button>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => setFormSubTab(2)}
                      sx={{ bgcolor: "#0284c7", textTransform: "none", fontWeight: 700 }}
                    >
                      Next: Airway, Dental & Systemic
                    </Button>
                  </Stack>
                </Stack>
              )}

              {/* --------------------------------------------------------------------- */}
              {/* SUBSECTION 3: AIRWAY, DENTAL CHART & SYSTEMIC REVIEWS                 */}
              {/* --------------------------------------------------------------------- */}
              {formSubTab === 2 && (
                <Stack spacing={2}>
                  {/* Accordion 3.1: Airway Assessment */}
                  <Accordion
                    expanded={Boolean(expandedSec2[0])}
                    onChange={() => setExpandedSec2((prev) => ({ ...prev, 0: !prev[0] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <HealthAndSafetyIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          3.1 Airway Assessment & Intubation Metrics
                        </Typography>
                        <Chip size="small" label={`Mallampati ${mallampati}`} color={mallampati === "Class III" || mallampati === "Class IV" ? "warning" : "default"} sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Mallampati Classification"
                            value={mallampati}
                            onChange={(e) => setMallampati(e.target.value)}
                          >
                            {MALLAMPATI_OPTIONS.map((m) => (
                              <MenuItem key={m.value} value={m.value}>
                                {m.value} ({m.desc})
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Mouth Opening"
                            value={mouthOpening}
                            onChange={(e) => setMouthOpening(e.target.value)}
                          >
                            <MenuItem value="ADEQUATE">Adequate (&gt; 3 fingers)</MenuItem>
                            <MenuItem value="MODERATE">Moderate (2-3 fingers)</MenuItem>
                            <MenuItem value="RESTRICTED">Restricted (&lt; 2 fingers)</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Mouth Opening (Fingers)"
                            value={mouthOpeningFingers}
                            onChange={(e) => setMouthOpeningFingers(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Thyromental Distance (cm)"
                            value={thyromentalDistance}
                            onChange={(e) => setThyromentalDistance(e.target.value)}
                            placeholder="Normal &gt; 6.5 cm"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Neck Movements / Mobility"
                            value={neckMovements}
                            onChange={(e) => setNeckMovements(e.target.value)}
                          >
                            <MenuItem value="NORMAL">Normal Extension & Flexion</MenuItem>
                            <MenuItem value="RESTRICTED">Restricted Neck Mobility</MenuItem>
                            <MenuItem value="RIGID">Rigid / Cervical Spine Instability</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Obstructive Sleep Apnea (OSA)"
                            value={osa}
                            onChange={(e) => setOsa(e.target.value)}
                          >
                            <MenuItem value="NIL">Nil / Low Risk</MenuItem>
                            <MenuItem value="SUSPECTED">Suspected (Snoring/Daytime Somnolence)</MenuItem>
                            <MenuItem value="CONFIRMED_CPAP">Confirmed OSA on CPAP</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={expectedDifficultAirway}
                                onChange={(e) => setExpectedDifficultAirway(e.target.checked)}
                              />
                            }
                            label="Anticipated Difficult Intubation / Airway"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={neckMass}
                                onChange={(e) => setNeckMass(e.target.checked)}
                              />
                            }
                            label="Neck Mass / Goitre / Swelling"
                          />
                        </Grid>
                        {neckMass && (
                          <Grid item xs={12} sm={8}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Neck Mass Description & Mobility"
                              placeholder="Size, location, fixity to trachea, retrosternal extension..."
                              value={neckMassDesc}
                              onChange={(e) => setNeckMassDesc(e.target.value)}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Accordion 3.2: Dental Chart */}
                  <DentalAirwaySection
                    expanded={Boolean(expandedSec2[1])}
                    onToggleExpand={() => setExpandedSec2((prev) => ({ ...prev, 1: !prev[1] }))}
                    dentalMarks={dentalMarks}
                    onToothClick={handleToothClick}
                    dentures={dentures}
                    onDenturesChange={setDentures}
                    patientInformed={patientInformed}
                    onPatientInformedChange={setPatientInformed}
                  />

                  {/* Accordion 3.3: Systemic Reviews */}
                  <Accordion
                    expanded={Boolean(expandedSec2[2])}
                    onChange={() => setExpandedSec2((prev) => ({ ...prev, 2: !prev[2] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <LocalHospitalIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          3.3 Systemic Reviews (CVS, Resp, Neuro, GI, Renal, Endocrine)
                        </Typography>
                        <Chip size="small" label={cvsNad && respNad && giNad && renalNad && endoNad ? "All Systems NAD" : "Findings Documented"} color={cvsNad && respNad && giNad && renalNad && endoNad ? "success" : "default"} sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <Grid container spacing={2}>
                        {/* CVS */}
                        <Grid item xs={12} sm={6}>
                          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>Cardiovascular System</Typography>
                              <FormControlLabel control={<Checkbox size="small" checked={cvsNad} onChange={(e) => setCvsNad(e.target.checked)} />} label="NAD" />
                            </Stack>
                            {!cvsNad && (
                              <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                  <FormControlLabel control={<Checkbox size="small" checked={cvsHtn} onChange={(e) => setCvsHtn(e.target.checked)} />} label="Hypertension" />
                                </Grid>
                                {cvsHtn && (
                                  <Grid item xs={6}>
                                    <TextField
                                      select
                                      fullWidth
                                      size="small"
                                      label="HTN Control"
                                      value={cvsHtnStatus}
                                      onChange={(e) => setCvsHtnStatus(e.target.value)}
                                    >
                                      <MenuItem value="WELL_CONTROLLED">Well Controlled</MenuItem>
                                      <MenuItem value="MODERATELY_CONTROLLED">Moderately Controlled</MenuItem>
                                      <MenuItem value="POORLY_CONTROLLED">Poorly Controlled</MenuItem>
                                      <MenuItem value="UNCONTROLLED">Uncontrolled</MenuItem>
                                    </TextField>
                                  </Grid>
                                )}
                                <Grid item xs={6}>
                                  <FormControlLabel control={<Checkbox size="small" checked={cvsIhd} onChange={(e) => setCvsIhd(e.target.checked)} />} label="IHD / CAD" />
                                </Grid>
                                <Grid item xs={6}>
                                  <FormControlLabel control={<Checkbox size="small" checked={cvsHf} onChange={(e) => setCvsHf(e.target.checked)} />} label="Heart Failure" />
                                </Grid>
                                {cvsHf && (
                                  <Grid item xs={6}>
                                    <TextField
                                      select
                                      fullWidth
                                      size="small"
                                      label="NYHA Class"
                                      value={cvsHfNyha}
                                      onChange={(e) => setCvsHfNyha(e.target.value)}
                                    >
                                      <MenuItem value="I">Class I</MenuItem>
                                      <MenuItem value="II">Class II</MenuItem>
                                      <MenuItem value="III">Class III</MenuItem>
                                      <MenuItem value="IV">Class IV</MenuItem>
                                    </TextField>
                                  </Grid>
                                )}
                                <Grid item xs={6}>
                                  <FormControlLabel control={<Checkbox size="small" checked={cvsDyslipidemia} onChange={(e) => setCvsDyslipidemia(e.target.checked)} />} label="Dyslipidemia" />
                                </Grid>
                                <Grid item xs={6}>
                                  <FormControlLabel control={<Checkbox size="small" checked={cvsPalpitation} onChange={(e) => setCvsPalpitation(e.target.checked)} />} label="Palpitations" />
                                </Grid>
                                <Grid item xs={6}>
                                  <TextField fullWidth size="small" label="LVEF (%)" value={cvsLvef} onChange={(e) => setCvsLvef(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField fullWidth size="small" label="Arrhythmias / AF" value={cvsArrhythmias} onChange={(e) => setCvsArrhythmias(e.target.value)} placeholder="e.g. Atrial fibrillation, PACs" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField fullWidth size="small" label="Valvular Disease" value={cvsValvular} onChange={(e) => setCvsValvular(e.target.value)} placeholder="e.g. Aortic stenosis, MR" />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField fullWidth size="small" label="Heart Sounds & Murmurs" value={cvsAuscHeart} onChange={(e) => setCvsAuscHeart(e.target.value)} />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField fullWidth size="small" label="Other CVS Findings" value={cvsOthers} onChange={(e) => setCvsOthers(e.target.value)} />
                                </Grid>
                              </Grid>
                            )}
                          </Paper>
                        </Grid>

                        {/* Respiratory */}
                        <Grid item xs={12} sm={6}>
                          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>Respiratory System</Typography>
                              <FormControlLabel control={<Checkbox size="small" checked={respNad} onChange={(e) => setRespNad(e.target.checked)} />} label="NAD" />
                            </Stack>
                            {!respNad && (
                              <Grid container spacing={1.5}>
                                <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={respAsthma} onChange={(e) => setRespAsthma(e.target.checked)} />} label="Asthma" /></Grid>
                                <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={respCopd} onChange={(e) => setRespCopd(e.target.checked)} />} label="COPD" /></Grid>
                                <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={respRecentUri} onChange={(e) => setRespRecentUri(e.target.checked)} />} label="Recent URI (&lt; 4w)" /></Grid>
                                <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={respTb} onChange={(e) => setRespTb(e.target.checked)} />} label="Past / Active TB" /></Grid>
                                <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={respCough} onChange={(e) => setRespCough(e.target.checked)} />} label="Persistent Cough" /></Grid>
                                <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={respDyspnea} onChange={(e) => setRespDyspnea(e.target.checked)} />} label="Dyspnea / Shortness of Breath" /></Grid>
                                <Grid item xs={12}>
                                  <TextField fullWidth size="small" label="Breath Sounds & Wheeze/Crepts" value={cvsAuscLungs} onChange={(e) => setCvsAuscLungs(e.target.value)} />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField fullWidth size="small" label="Other Respiratory Findings" value={respOthers} onChange={(e) => setRespOthers(e.target.value)} />
                                </Grid>
                              </Grid>
                            )}
                          </Paper>
                        </Grid>

                        {/* Central Nervous System */}
                        <Grid item xs={12} sm={4}>
                          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>Central Nervous System</Typography>
                            <Grid container spacing={1}>
                              <Grid item xs={12}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label="Mental Status / Consciousness"
                                  value={neuroMental}
                                  onChange={(e) => setNeuroMental(e.target.value)}
                                >
                                  <MenuItem value="NORMAL">Alert & Oriented (Normal)</MenuItem>
                                  <MenuItem value="CONFUSED">Confused / Delirious</MenuItem>
                                  <MenuItem value="DROWSY">Drowsy / Lethargic</MenuItem>
                                  <MenuItem value="OBTUNDED">Obtunded / Comatose</MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item xs={12}><FormControlLabel control={<Checkbox size="small" checked={neuroCva} onChange={(e) => setNeuroCva(e.target.checked)} />} label="CVA / Stroke / TIA" /></Grid>
                              <Grid item xs={12}><FormControlLabel control={<Checkbox size="small" checked={neuroSeizures} onChange={(e) => setNeuroSeizures(e.target.checked)} />} label="Seizures / Epilepsy" /></Grid>
                              <Grid item xs={12}><FormControlLabel control={<Checkbox size="small" checked={neuroSyncope} onChange={(e) => setNeuroSyncope(e.target.checked)} />} label="Syncope / Blackouts" /></Grid>
                              <Grid item xs={12}><FormControlLabel control={<Checkbox size="small" checked={neuroWeakness} onChange={(e) => setNeuroWeakness(e.target.checked)} />} label="Focal Weakness / Deficits" /></Grid>
                              <Grid item xs={12}><FormControlLabel control={<Checkbox size="small" checked={neuroMyopathies} onChange={(e) => setNeuroMyopathies(e.target.checked)} />} label="Myopathies / Neuromuscular" /></Grid>
                              <Grid item xs={12}><FormControlLabel control={<Checkbox size="small" checked={neuroChronicPain} onChange={(e) => setNeuroChronicPain(e.target.checked)} />} label="Chronic Neuropathic Pain" /></Grid>
                              <Grid item xs={12}><TextField fullWidth size="small" label="Neuro Notes" value={neuroOthers} onChange={(e) => setNeuroOthers(e.target.value)} /></Grid>
                            </Grid>
                          </Paper>
                        </Grid>

                        {/* GI & Renal */}
                        <Grid item xs={12} sm={4}>
                          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>GI & Renal Systems</Typography>
                              <FormControlLabel control={<Checkbox size="small" checked={giNad && renalNad} onChange={(e) => { setGiNad(e.target.checked); setRenalNad(e.target.checked); }} />} label="NAD" />
                            </Stack>
                            <Grid container spacing={1}>
                              <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={giGerd} onChange={(e) => setGiGerd(e.target.checked)} />} label="GERD / Acid Reflux" /></Grid>
                              <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={giJaundice} onChange={(e) => setGiJaundice(e.target.checked)} />} label="Jaundice / Hepatobiliary" /></Grid>
                              <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={giCirrhosis} onChange={(e) => setGiCirrhosis(e.target.checked)} />} label="Liver Cirrhosis / Portal HTN" /></Grid>
                              <Grid item xs={12}><TextField fullWidth size="small" label="Other GI Findings" value={giOthers} onChange={(e) => setGiOthers(e.target.value)} /></Grid>
                              <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={renalCrf} onChange={(e) => setRenalCrf(e.target.checked)} />} label="CKD / Renal Insufficiency" /></Grid>
                              <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={renalDialysis} onChange={(e) => setRenalDialysis(e.target.checked)} />} label="Active Dialysis" /></Grid>
                              <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={renalEsrd} onChange={(e) => setRenalEsrd(e.target.checked)} />} label="ESRD (End Stage)" /></Grid>
                              {renalCrf && (
                                <Grid item xs={6}>
                                  <TextField fullWidth size="small" type="number" label="CCr / GFR (mL/min)" value={renalCcr} onChange={(e) => setRenalCcr(e.target.value)} placeholder="e.g. 45" />
                                </Grid>
                              )}
                              <Grid item xs={12}><TextField fullWidth size="small" label="Other Renal Findings" value={renalOthers} onChange={(e) => setRenalOthers(e.target.value)} /></Grid>
                            </Grid>
                          </Paper>
                        </Grid>

                        {/* Endocrine */}
                        <Grid item xs={12} sm={4}>
                          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>Endocrine & Metabolic</Typography>
                              <FormControlLabel control={<Checkbox size="small" checked={endoNad} onChange={(e) => setEndoNad(e.target.checked)} />} label="NAD" />
                            </Stack>
                            <Grid container spacing={1}>
                              <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={endoDm} onChange={(e) => setEndoDm(e.target.checked)} />} label="Diabetes Mellitus" /></Grid>
                              {endoDm && (
                                <Grid item xs={6}>
                                  <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Diabetes Type"
                                    value={endoDmType}
                                    onChange={(e) => setEndoDmType(e.target.value)}
                                  >
                                    <MenuItem value="TYPE_1">Type 1 DM</MenuItem>
                                    <MenuItem value="TYPE_2">Type 2 DM</MenuItem>
                                    <MenuItem value="GESTATIONAL">Gestational DM</MenuItem>
                                  </TextField>
                                </Grid>
                              )}
                              <Grid item xs={6}><TextField fullWidth size="small" label="HbA1c (%)" value={endoHba1c} onChange={(e) => setEndoHba1c(e.target.value)} /></Grid>
                              <Grid item xs={6}><FormControlLabel control={<Checkbox size="small" checked={endoAdrenal} onChange={(e) => setEndoAdrenal(e.target.checked)} />} label="Adrenal / Steroid Dep." /></Grid>
                              <Grid item xs={6}>
                                <TextField select fullWidth size="small" label="Thyroid" value={endoThyroid} onChange={(e) => setEndoThyroid(e.target.value)}>
                                  <MenuItem value="NORMAL">Euthyroid</MenuItem>
                                  <MenuItem value="HYPOTHYROID">Hypothyroid</MenuItem>
                                  <MenuItem value="HYPERTHYROID">Hyperthyroid</MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item xs={6}>
                                <TextField fullWidth size="small" label="LMP (Last Menstrual Period)" placeholder="DD/MM/YYYY or N/A" value={endoLmp} onChange={(e) => setEndoLmp(e.target.value)} />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Other Endocrine / Metabolic" value={endoOthers} onChange={(e) => setEndoOthers(e.target.value)} />
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
                    <Button variant="outlined" onClick={() => setFormSubTab(1)} sx={{ textTransform: "none" }}>
                      Previous: History & Habits
                    </Button>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => setFormSubTab(3)}
                      sx={{ bgcolor: "#0284c7", textTransform: "none", fontWeight: 700 }}
                    >
                      Next: CPOE Orders & Results
                    </Button>
                  </Stack>
                </Stack>
              )}

              {/* --------------------------------------------------------------------- */}
              {/* SUBSECTION 4: LAB & RADIOLOGY ORDERS (CPOE INTEGRATION)               */}
              {/* --------------------------------------------------------------------- */}
              {formSubTab === 3 && (
                <Stack spacing={2}>
                  {/* Accordion 4.1: Pre-Op Diagnostic Parameters & Outside Investigations */}
                  <Accordion
                    expanded={Boolean(expandedSec3[0])}
                    onChange={() => setExpandedSec3((prev) => ({ ...prev, 0: !prev[0] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <ScienceIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          4.1 Pre-Operative Diagnostic Parameters & Outside Investigations
                        </Typography>
                        <Chip
                          size="small"
                          label={`Hb ${cbcHb || "--"} g/dL · Plt ${cbcPlt || "--"} · Cr ${chemCr || "--"} · INR ${coagInr || "--"}`}
                          sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
                        />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                        Structured baseline parameters auto-populated from hospital laboratory (LIS) and imaging (RIS) systems, or entered directly from outside paper investigation reports.
                      </Typography>

                      <Grid container spacing={2.5}>
                        {/* 1. Complete Blood Count (CBC) */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 1.5, bgcolor: "#f8fafc" }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                Complete Blood Count (CBC / Hemogram)
                              </Typography>
                              <TextField
                                size="small"
                                type="date"
                                label="Sample Date"
                                InputLabelProps={{ shrink: true }}
                                value={cbcDate}
                                onChange={(e) => setCbcDate(e.target.value)}
                                sx={{ width: 150 }}
                              />
                            </Stack>
                            <Grid container spacing={1.5}>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Hb (g/dL)"
                                  value={cbcHb}
                                  onChange={(e) => setCbcHb(e.target.value)}
                                  placeholder="13.5"
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="WBC (/µL)"
                                  value={cbcWbc}
                                  onChange={(e) => setCbcWbc(e.target.value)}
                                  placeholder="7500"
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Hematocrit (%)"
                                  value={cbcHct}
                                  onChange={(e) => setCbcHct(e.target.value)}
                                  placeholder="40"
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Platelets (×10³/µL)"
                                  value={cbcPlt}
                                  onChange={(e) => setCbcPlt(e.target.value)}
                                  placeholder="250"
                                />
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>

                        {/* 2. Coagulation Profile */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 1.5, bgcolor: "#f8fafc" }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                Coagulation Profile (Hemostasis)
                              </Typography>
                              <TextField
                                size="small"
                                type="date"
                                label="Sample Date"
                                InputLabelProps={{ shrink: true }}
                                value={coagDate}
                                onChange={(e) => setCoagDate(e.target.value)}
                                sx={{ width: 150 }}
                              />
                            </Stack>
                            <Grid container spacing={1.5}>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="PT (sec)"
                                  value={coagPt}
                                  onChange={(e) => setCoagPt(e.target.value)}
                                  placeholder="12.0"
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="INR"
                                  value={coagInr}
                                  onChange={(e) => setCoagInr(e.target.value)}
                                  placeholder="1.0"
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="APTT (sec)"
                                  value={coagAptt}
                                  onChange={(e) => setCoagAptt(e.target.value)}
                                  placeholder="30"
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="PTT (sec)"
                                  value={coagPtt}
                                  onChange={(e) => setCoagPtt(e.target.value)}
                                  placeholder="32"
                                />
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>

                        {/* 3. Renal Function & Electrolytes (Biochemistry) */}
                        <Grid item xs={12}>
                          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 1.5, bgcolor: "#f8fafc" }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                Renal Function & Serum Electrolytes (Biochemistry)
                              </Typography>
                              <TextField
                                size="small"
                                type="date"
                                label="Sample Date"
                                InputLabelProps={{ shrink: true }}
                                value={chemDate}
                                onChange={(e) => setChemDate(e.target.value)}
                                sx={{ width: 150 }}
                              />
                            </Stack>
                            <Grid container spacing={1.5}>
                              <Grid item xs={6} sm={3} md={1.7}>
                                <TextField fullWidth size="small" label="Na+ (mEq/L)" value={chemNa} onChange={(e) => setChemNa(e.target.value)} placeholder="140" />
                              </Grid>
                              <Grid item xs={6} sm={3} md={1.7}>
                                <TextField fullWidth size="small" label="K+ (mEq/L)" value={chemK} onChange={(e) => setChemK(e.target.value)} placeholder="4.2" />
                              </Grid>
                              <Grid item xs={6} sm={3} md={1.7}>
                                <TextField fullWidth size="small" label="Cl- (mEq/L)" value={chemCl} onChange={(e) => setChemCl(e.target.value)} placeholder="102" />
                              </Grid>
                              <Grid item xs={6} sm={3} md={1.7}>
                                <TextField fullWidth size="small" label="HCO3- (mEq/L)" value={chemHco3} onChange={(e) => setChemHco3(e.target.value)} placeholder="24" />
                              </Grid>
                              <Grid item xs={6} sm={4} md={1.7}>
                                <TextField fullWidth size="small" label="BUN (mg/dL)" value={chemBun} onChange={(e) => setChemBun(e.target.value)} placeholder="15" />
                              </Grid>
                              <Grid item xs={6} sm={4} md={1.7}>
                                <TextField fullWidth size="small" label="Creatinine (mg/dL)" value={chemCr} onChange={(e) => setChemCr(e.target.value)} placeholder="0.9" />
                              </Grid>
                              <Grid item xs={12} sm={4} md={1.8}>
                                <TextField fullWidth size="small" label="Glucose (mg/dL)" value={chemGlu} onChange={(e) => setChemGlu(e.target.value)} placeholder="110" />
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>

                        {/* 4. Cardiopulmonary & Outside Diagnostic Reports */}
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Chest X-Ray (CXR) Findings"
                            value={cxrFindings}
                            onChange={(e) => setCxrFindings(e.target.value)}
                            placeholder="e.g. Normal cardiothoracic ratio, clear lung fields, no focal consolidation..."
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="12-Lead Electrocardiogram (ECG) Findings"
                            value={ecgFindings}
                            onChange={(e) => setEcgFindings(e.target.value)}
                            placeholder="e.g. Normal sinus rhythm, rate 76 bpm, normal axis, no ST-T changes or Q waves..."
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Other Laboratory Investigations"
                            value={otherLabs}
                            onChange={(e) => setOtherLabs(e.target.value)}
                            placeholder="e.g. LFTs (Bilirubin, SGOT, SGPT, Albumin), Viral Markers (HIV, HBsAg, HCV), Urinalysis..."
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Other Diagnostic Investigations (ECHO, USG, PFT, CT)"
                            value={otherInvestigations}
                            onChange={(e) => setOtherInvestigations(e.target.value)}
                            placeholder="e.g. 2D Echo: LVEF 60%, normal LV wall motion; USG Abdomen: normal liver and kidneys..."
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Accordion 4.2: CPOE Live Orders & Reports Feed */}
                  <Accordion
                    expanded={Boolean(expandedSec3[1])}
                    onChange={() => setExpandedSec3((prev) => ({ ...prev, 1: !prev[1] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <MedicationIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          4.2 Computerized Physician Order Entry (CPOE) Live Orders & Reports Feed
                        </Typography>
                        <Chip
                          size="small"
                          label={`${cpoeOrders.length} Orders (${labCount} Lab, ${radCount} Rad)`}
                          color={cpoeOrders.length > 0 ? "primary" : "default"}
                          sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
                        />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <PacCpoeSection
                        cpoeOrders={cpoeOrders}
                        loadingCpoeOrders={loadingCpoeOrders}
                        onRefreshCpoe={loadCpoeOrders}
                        onOpenOrderDrawer={() => setOrderDrawerOpen(true)}
                      />
                    </AccordionDetails>
                  </Accordion>

                  <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
                    <Button variant="outlined" onClick={() => setFormSubTab(2)} sx={{ textTransform: "none" }}>
                      Previous: Airway & Systemic
                    </Button>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => setFormSubTab(4)}
                      sx={{ bgcolor: "#0284c7", textTransform: "none", fontWeight: 700 }}
                    >
                      Next: Anesthesia Plan & Logistics
                    </Button>
                  </Stack>
                </Stack>
              )}

              {/* --------------------------------------------------------------------- */}
              {/* SUBSECTION 5: ANESTHESIA PLAN & LOGISTICS                             */}
              {/* --------------------------------------------------------------------- */}
              {formSubTab === 4 && (
                <Stack spacing={2}>
                  {/* Accordion 5.1: Proposed Technique & Pre-medication */}
                  <Accordion
                    expanded={Boolean(expandedSec4[0])}
                    onChange={() => setExpandedSec4((prev) => ({ ...prev, 0: !prev[0] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <HealthAndSafetyIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          5.1 Proposed Anesthesia Technique & Pre-medication Plan
                        </Typography>
                        <Chip size="small" label={anesthesiaPlan} sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Proposed Anesthetic Technique"
                            value={anesthesiaPlan}
                            onChange={(e) => setAnesthesiaPlan(e.target.value)}
                          >
                            {ANESTHESIA_PLANS.map((plan) => (
                              <MenuItem key={plan} value={plan}>
                                {plan}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", display: "block", mb: 0.5 }}>
                            Quick Pre-medication Templates:
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {QUICK_PREMEDS.map((med) => (
                              <Chip
                                key={med}
                                size="small"
                                label={med.split(" ")[1]}
                                onClick={() =>
                                  setSuggestedMeds((prev) => (prev ? `${prev}\n${med}` : med))
                                }
                                sx={{ cursor: "pointer", mb: 0.5 }}
                              />
                            ))}
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Pre-Operative Medication Orders (Auto-Bridged to IPD Nursing Tasks)"
                            value={suggestedMeds}
                            onChange={(e) => setSuggestedMeds(e.target.value)}
                            placeholder={"One premedication per line, e.g.\nTab Pantoprazole 40mg PO\nInj Cefazolin 1g IV on call"}
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Accordion 5.2: Cross-Specialty Consultation & Referral */}
                  <Accordion
                    expanded={Boolean(expandedSec4[1])}
                    onChange={() => setExpandedSec4((prev) => ({ ...prev, 1: !prev[1] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <PersonAddIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          5.2 Cross-Specialty Pre-Operative Consultation & Referral
                        </Typography>
                        <Chip
                          size="small"
                          label={consultRequired ? "Consultation Active" : "No Referral Required"}
                          color={consultRequired ? "primary" : "default"}
                          sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
                        />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                        Select a department and consultant physician to request pre-operative medical clearance. The patient will automatically appear in their Doctor Console under 'Referred Patients' (ReferredToMe), and all clinical notes provided by the anesthesiologist will be directly visible to the consultant doctor and the attending ward nurse.
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Consultation Department"
                            value={selectedConsultDept}
                            onChange={(e) => handleDeptChange(e.target.value)}
                            disabled={loadingDepts}
                          >
                            <MenuItem value="">
                              <em>Select Department</em>
                            </MenuItem>
                            {medicalDepartments.map((dept) => (
                              <MenuItem key={dept.id || dept.name} value={dept.name}>
                                {dept.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Consultant Doctor"
                            value={selectedConsultDoctorId}
                            onChange={(e) => setSelectedConsultDoctorId(e.target.value)}
                            disabled={!selectedConsultDept || loadingDocs}
                          >
                            <MenuItem value="">
                              <em>{loadingDocs ? "Loading Doctors..." : "Select Doctor"}</em>
                            </MenuItem>
                            {availableDoctors.map((doc) => (
                              <MenuItem key={doc.doctor_id || doc.id} value={doc.doctor_id || doc.id}>
                                Dr. {doc.name} {doc.specialization ? `(${doc.specialization})` : ""}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<PersonAddIcon />}
                            disabled={referringInProgress || !selectedConsultDoctorId}
                            onClick={handleRequestReferral}
                            sx={{ height: 40, bgcolor: "#0f766e", textTransform: "none", fontWeight: 700 }}
                          >
                            {referringInProgress ? "Sending Referral..." : "Request Specialty Clearance"}
                          </Button>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Reason for Consultation & Specific Questions for Consultant"
                            value={consultNotes}
                            onChange={(e) => setConsultNotes(e.target.value)}
                            placeholder="e.g. Cardiology clearance requested for uncontrolled HTN (BP 160/100) and history of CAD prior to major abdominal surgery under General Anesthesia. Kindly evaluate for cardiac risk stratification and medication optimization."
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Specialty Clearance Status"
                            value={consultAccepted ? "CLEARED" : "DEFERRED"}
                            onChange={(e) => setConsultAccepted(e.target.value === "CLEARED")}
                          >
                            <MenuItem value="CLEARED">Cleared / Accepted for Surgery</MenuItem>
                            <MenuItem value="DEFERRED">Deferred / Not Cleared / Pending Review</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Clearance Remarks & Recommendations"
                            value={consultReason}
                            onChange={(e) => setConsultReason(e.target.value)}
                            placeholder="e.g. Cleared for GA with low threshold for intra-op invasive BP monitoring..."
                          />
                        </Grid>
                      </Grid>

                      {/* Active Referrals List */}
                      {activeReferrals.length > 0 && (
                        <Box sx={{ mt: 2.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 1 }}>
                            Active Cross-Specialty Referrals for This Case:
                          </Typography>
                          <Stack spacing={1}>
                            {activeReferrals.map((ref, idx) => (
                              <Paper key={idx} elevation={0} sx={{ p: 1.5, border: "1px solid #cbd5e1", borderRadius: 1.5, bgcolor: "#f8fafc" }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                                    Dr. {ref.name} — {ref.specialty}
                                  </Typography>
                                  <Chip size="small" label={ref.status} color="info" sx={{ fontWeight: 700 }} />
                                </Stack>
                                <Typography variant="caption" sx={{ color: "#475569", display: "block", mt: 0.5 }}>
                                  <strong>Clinical Notes:</strong> {ref.notes || "Pre-operative evaluation requested."}
                                </Typography>
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {/* Accordion 5.3: Blood Component Logistics */}
                  <Accordion
                    expanded={Boolean(expandedSec4[2])}
                    onChange={() => setExpandedSec4((prev) => ({ ...prev, 2: !prev[2] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <HealthAndSafetyIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          5.3 Blood Component Reservation & Transfusion Logistics
                        </Typography>
                        <Chip
                          size="small"
                          label={bloodPrbc || bloodWb || bloodFfp || bloodPlt ? `${Number(bloodPrbc || 0) + Number(bloodWb || 0) + Number(bloodFfp || 0) + Number(bloodPlt || 0)} Units Reserved` : "None / Type & Screen"}
                          sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
                        />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="PRBC (Packed Red Cells)"
                            value={bloodPrbc}
                            onChange={(e) => setBloodPrbc(e.target.value)}
                            placeholder="Units (e.g. 2)"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Whole Blood (Units)"
                            value={bloodWb}
                            onChange={(e) => setBloodWb(e.target.value)}
                            placeholder="Units"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="FFP (Fresh Frozen Plasma)"
                            value={bloodFfp}
                            onChange={(e) => setBloodFfp(e.target.value)}
                            placeholder="Units"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Platelet Concentrates (RDP/SDP)"
                            value={bloodPlt}
                            onChange={(e) => setBloodPlt(e.target.value)}
                            placeholder="Units"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Blood Cross-Match & Transfusion Directives"
                            value={bloodRemarks}
                            onChange={(e) => setBloodRemarks(e.target.value)}
                            placeholder="e.g. Cross-match 2 units PRBC on call, Irradiated, CMV-negative, Leukoreduced, Antibody screen negative..."
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Accordion 5.4: Postoperative Care & ICU/HDU */}
                  <Accordion
                    expanded={Boolean(expandedSec4[3])}
                    onChange={() => setExpandedSec4((prev) => ({ ...prev, 3: !prev[3] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <LocalHospitalIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          5.4 Postoperative Care & ICU/HDU Step-Up Reservation
                        </Typography>
                        <Chip
                          size="small"
                          label={icuBooking ? "ICU Bed Reserved" : "Standard PACU"}
                          color={icuBooking ? "warning" : "default"}
                          sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
                        />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={icuBooking}
                            onChange={(e) => setIcuBooking(e.target.checked)}
                          />
                        }
                        label="ICU / HDU Bed Reservation Required Post-Surgery (Step-Up Monitoring / Ventilator)"
                      />
                    </AccordionDetails>
                  </Accordion>

                  {/* Accordion 5.5: Risk Discussion & Informed Consent */}
                  <Accordion
                    expanded={Boolean(expandedSec4[4])}
                    onChange={() => setExpandedSec4((prev) => ({ ...prev, 4: !prev[4] }))}
                    sx={{ border: "1px solid #e2e8f0", borderRadius: "10px !important", overflow: "hidden", "&:before": { display: "none" } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />} sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <HealthAndSafetyIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          5.5 Anesthetic Risk Discussion & Informed Consent Alignment
                        </Typography>
                        <Chip size="small" label={consentRiskLevel} color={consentRiskLevel === "HIGH" ? "error" : "default"} sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Risk Discussed With"
                            value={riskDiscussedWith}
                            onChange={(e) => setRiskDiscussedWith(e.target.value)}
                          >
                            <MenuItem value="PATIENT">Patient Themselves</MenuItem>
                            <MenuItem value="PARENTS">Parents (Pediatric)</MenuItem>
                            <MenuItem value="SPOUSE">Spouse</MenuItem>
                            <MenuItem value="KIN">Next of Kin / Authorized Representative</MenuItem>
                            <MenuItem value="LEGAL_GUARDIAN">Legal Guardian / Power of Attorney</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Relative Name & Relationship"
                            value={kinNameRelation}
                            onChange={(e) => setKinNameRelation(e.target.value)}
                            placeholder="e.g. John Doe (Brother)"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Informed Consent Risk Level"
                            value={consentRiskLevel}
                            onChange={(e) => setConsentRiskLevel(e.target.value)}
                          >
                            <MenuItem value="LOW">Low Risk</MenuItem>
                            <MenuItem value="NORMAL">Average / Normal Risk</MenuItem>
                            <MenuItem value="HIGH">High Risk</MenuItem>
                            <MenuItem value="EXTREME">Extreme / High-Risk Consent Required</MenuItem>
                          </TextField>
                        </Grid>
                        {(consentRiskLevel === "HIGH" || consentRiskLevel === "EXTREME" || consentRiskDetails) && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              label="Specific High-Risk Anesthetic Complications Discussed with Patient / Family"
                              value={consentRiskDetails}
                              onChange={(e) => setConsentRiskDetails(e.target.value)}
                              placeholder="e.g. Perioperative cardiac event, prolonged postoperative mechanical ventilation, unanticipated difficult airway, emergency tracheostomy..."
                            />
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={agreedToPlan}
                                onChange={(e) => setAgreedToPlan(e.target.checked)}
                              />
                            }
                            label="Patient / Surrogate Counseled in Understandable Language & Fully Agreed to Anesthesia Plan"
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
                    <Button variant="outlined" onClick={() => setFormSubTab(3)} sx={{ textTransform: "none" }}>
                      Previous: CPOE Orders
                    </Button>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => setActiveStage(2)}
                      sx={{ bgcolor: "#0f766e", textTransform: "none", fontWeight: 700 }}
                    >
                      Proceed to Step 2: Fitness Outcome & Orders
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Box>
          )}

          {/* ========================================================================= */}
          {/* STAGE 2: FITNESS OUTCOME & PRE-OP ORDERS                                  */}
          {/* ========================================================================= */}
          {activeStage === 2 && (
            <Stack spacing={2.5}>
              <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#fff" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
                  Pre-Operative Fitness Clearance Decision
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="ASA Physical Status Classification"
                      value={asaClass}
                      onChange={(e) => setAsaClass(e.target.value)}
                    >
                      {ASA_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={asaEmergency}
                          onChange={(e) => setAsaEmergency(e.target.checked)}
                        />
                      }
                      label="Emergency Modifier (E) — Unscheduled Emergency Surgery"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Mallampati Airway Score"
                      value={mallampati}
                      onChange={(e) => setMallampati(e.target.value)}
                    >
                      {MALLAMPATI_OPTIONS.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                          {m.value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Outcome Decision Radio Group */}
                  <Grid item xs={12}>
                    <FormLabel component="legend" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
                      Fitness Clearance Outcome
                    </FormLabel>
                    <RadioGroup
                      row
                      value={outcome}
                      onChange={(e) => setOutcome(e.target.value)}
                      sx={{ gap: 2 }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          border: outcome === "FIT" ? "2px solid #16a34a" : "1px solid #cbd5e1",
                          borderRadius: 2,
                          bgcolor: outcome === "FIT" ? "#f0fdf4" : "#fff",
                          cursor: "pointer",
                          flex: 1,
                        }}
                        onClick={() => setOutcome("FIT")}
                      >
                        <FormControlLabel
                          value="FIT"
                          control={<Radio color="success" />}
                          label={
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#166534" }}>
                                FIT FOR SURGERY
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Cleared for planned anesthesia technique. Advance case to PAC Cleared.
                              </Typography>
                            </Box>
                          }
                        />
                      </Paper>

                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          border: outcome === "OPTIMIZE" ? "2px solid #ea580c" : "1px solid #cbd5e1",
                          borderRadius: 2,
                          bgcolor: outcome === "OPTIMIZE" ? "#fff7ed" : "#fff",
                          cursor: "pointer",
                          flex: 1,
                        }}
                        onClick={() => setOutcome("OPTIMIZE")}
                      >
                        <FormControlLabel
                          value="OPTIMIZE"
                          control={<Radio color="warning" />}
                          label={
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#c2410c" }}>
                                FIT WITH OPTIMIZATION
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Conditional clearance. Optimization directives and re-evaluation required.
                              </Typography>
                            </Box>
                          }
                        />
                      </Paper>

                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          border: outcome === "UNFIT" ? "2px solid #dc2626" : "1px solid #cbd5e1",
                          borderRadius: 2,
                          bgcolor: outcome === "UNFIT" ? "#fef2f2" : "#fff",
                          cursor: "pointer",
                          flex: 1,
                        }}
                        onClick={() => setOutcome("UNFIT")}
                      >
                        <FormControlLabel
                          value="UNFIT"
                          control={<Radio color="error" />}
                          label={
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#991b1b" }}>
                                UNFIT / POSTPONE SURGERY
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Defer surgery due to active systemic instability or high anesthetic mortality.
                              </Typography>
                            </Box>
                          }
                        />
                      </Paper>
                    </RadioGroup>
                  </Grid>

                  {outcome !== "FIT" && (
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Next Review / Re-evaluation Date"
                        InputLabelProps={{ shrink: true }}
                        value={nextReviewDate}
                        onChange={(e) => setNextReviewDate(e.target.value)}
                        required
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Clinical Anesthesiology Notes / Reasons for Deferral"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Specify rationale, clinical risk factors, optimization instructions, or required clearances..."
                      required={outcome !== "FIT"}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Fasting & Pre-Op Ward Instructions (NPO Guidelines)"
                      value={optInstructions}
                      onChange={(e) => setOptInstructions(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Day-of-Surgery Reassessment Notes"
                      value={reassessmentNotes}
                      onChange={(e) => setReassessmentNotes(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Stack>
          )}
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions sx={{ px: 3, py: 2, bgcolor: "#f1f5f9", borderTop: "1px solid #cbd5e1", justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={onClose} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            {activeStage === 2 && (
              <Button variant="outlined" onClick={() => setActiveStage(1)} sx={{ textTransform: "none" }}>
                Back to Stage 1
              </Button>
            )}
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            {lockedReason ? (
              <Typography variant="body2" sx={{ color: "#b45309", fontSize: "0.8rem", fontWeight: 600 }}>
                {lockedReason}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                Clearance will record under: <strong>{sessionStorage.getItem("userName") || "Anaesthetist"}</strong>
              </Typography>
            )}
            <Button
              variant="contained"
              size="medium"
              disabled={saving || Boolean(lockedReason)}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleSave}
              sx={{
                bgcolor: outcome === "FIT" ? "#16a34a" : "#ea580c",
                fontWeight: 700,
                textTransform: "none",
                px: 3,
                "&:hover": { bgcolor: outcome === "FIT" ? "#15803d" : "#c2410c" },
              }}
            >
              {saving ? "Saving Assessment..." : outcome === "FIT" ? "Save & Clear Patient for Surgery" : `Save Assessment (${outcome})`}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* CPOE Order Management Drawer Trigger */}
      {orderDrawerOpen && (
        <OrderManagementDrawer
          open={orderDrawerOpen}
          onClose={() => {
            setOrderDrawerOpen(false);
            loadCpoeOrders();
          }}
          patient={drawerPatient(otCase)}
        />
      )}


      {/* ========================================================================= */}
      {/* PRINT VIEW MODAL WITH OFFICIAL HOSPITAL MIS LETTERHEAD & SIGNATURES        */}
      {/* ========================================================================= */}
      {showPrintView && (
        <Dialog open onClose={() => setShowPrintView(false)} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Pre-Anesthesia Assessment Form (Official MIS Printable View)
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{ bgcolor: "#0f766e" }}
              >
                Print Sheet
              </Button>
              <IconButton size="small" onClick={() => setShowPrintView(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 4, bgcolor: "#fff", color: "#000", fontFamily: "serif" }}>
            {/* Standard Hospital MIS Letterhead */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 2, mb: 2, borderBottom: "2px solid #000" }}>
              {facilityLogo && (
                <Box component="img" src={facilityLogo} alt="Hospital Logo" sx={{ maxHeight: 60, maxWidth: 160, objectFit: "contain" }} />
              )}
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h6" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1.2 }}>
                  {facilityIdentity.name}
                </Typography>
                <Typography variant="caption" sx={{ display: "block", color: "#333" }}>
                  {facilityDetails.address} · Contact: {facilityDetails.contact}
                </Typography>
                <Typography variant="caption" sx={{ display: "block", fontStyle: "italic", fontWeight: 700 }}>
                  NABH Accredited Healthcare Institution · Department of Anaesthesiology & Perioperative Care
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: 1 }}>
                PRE-ANESTHESIA ASSESSMENT FORM & CLEARANCE RECORD
              </Typography>
            </Box>

            {/* Patient Header Grid */}
            <Table size="small" sx={{ mb: 2, border: "1px solid #000" }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000" }}><strong>Patient Name:</strong> {otCase.patient_name}</TableCell>
                  <TableCell sx={{ border: "1px solid #000" }}><strong>Pre-Op Diagnosis:</strong> {diagnosis || "—"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000" }}><strong>IPD / MRD No:</strong> {otCase.health_record_id || otCase.patient_id} &nbsp;&nbsp; <strong>Age / Sex:</strong> {otCase.age}y / {otCase.gender}</TableCell>
                  <TableCell sx={{ border: "1px solid #000" }}><strong>Planned Procedure:</strong> {procedurePlanned || "—"} &nbsp;&nbsp; <strong>Date:</strong> {procedureDate || otCase.next_booking_start || "—"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000" }}><strong>Ward / Bed:</strong> {wardName} / {bedLabel} &nbsp;&nbsp; <strong>Blood Group:</strong> {bloodGroup || otCase.blood_group || "—"}</TableCell>
                  <TableCell sx={{ border: "1px solid #000" }}><strong>Dept:</strong> {department} &nbsp;&nbsp; <strong>Body Wt:</strong> {weightKg} kg &nbsp;&nbsp; <strong>Height:</strong> {heightCm} cm &nbsp;&nbsp; <strong>BMI:</strong> {computedBmi}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* 1. Medical History & Surgeries */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>1. Medical History & Previous Surgical Interventions:</Typography>
              <Typography variant="body2" sx={{ pl: 1, minHeight: 28, borderBottom: "1px dotted #000" }}>
                {summaryHistory || "No significant medical history documented."}
              </Typography>
            </Box>

            <Table size="small" sx={{ mb: 1.5, border: "1px solid #000" }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                  <TableCell sx={{ border: "1px solid #000" }}><strong>Previous Surgery</strong></TableCell>
                  <TableCell sx={{ border: "1px solid #000" }}><strong>Year</strong></TableCell>
                  <TableCell sx={{ border: "1px solid #000" }}><strong>Anesthesia Type</strong></TableCell>
                  <TableCell sx={{ border: "1px solid #000" }}><strong>Complications</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previousSurgeries.length > 0 ? (
                  previousSurgeries.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell sx={{ border: "1px solid #000" }}>{s.surgery_name}</TableCell>
                      <TableCell sx={{ border: "1px solid #000" }}>{s.year}</TableCell>
                      <TableCell sx={{ border: "1px solid #000" }}>{s.anesthesia_type}</TableCell>
                      <TableCell sx={{ border: "1px solid #000" }}>{s.complications}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ border: "1px solid #000", textAlign: "center" }}>None Documented</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Current Meds, Habits & Allergies */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2">
                <strong>Current Medications:</strong> {currentMeds || "None"} &nbsp;&nbsp;|&nbsp;&nbsp; <strong>Known Allergies:</strong> {allergies || "No Known Drug Allergies (NKDA)"}
              </Typography>
              <Typography variant="body2">
                <strong>Personal Habits:</strong> Smoking: {smokingPacks ? `${smokingPacks} ppd × ${smokingYears}y` : "No"} &nbsp;|&nbsp; Alcohol: {alcohol} &nbsp;|&nbsp; Substance Use: {drugAbuse || "Nil"} &nbsp;|&nbsp; Family Anesth Complications: {familyAnesthComp ? familyAnesthCompDesc : "Negative"}
              </Typography>
            </Box>

            {/* Airway & Vitals */}
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>2. Physical Examination, Baseline Vitals & Airway Assessment:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Vital Signs:</strong> BP: {bpSys}/{bpDia} mmHg, Pulse: {pulsePerMin}/min, RR: {rrPerMin}/min, SpO2: {spo2Percent}%, Temp: {tempC}°F, Pallor: {pallor} &nbsp;&nbsp;|&nbsp;&nbsp; <strong>Fasting / NPO:</strong> {fastingHours ? `${fastingHours} hrs` : "Not specified"}
            </Typography>
            {physicalNotes && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Inspection Notes:</strong> {physicalNotes}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Airway:</strong> Mallampati: {mallampati}, Mouth Opening: {mouthOpening} ({mouthOpeningFingers}f), Thyromental Distance: {thyromentalDistance}cm, Neck: {neckMovements}, OSA: {osa}, Difficult Airway Anticipated: {expectedDifficultAirway ? "YES" : "NO"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Dentition:</strong> Dentures: {dentures ? "Yes" : "No"}, Loose Teeth: {Object.entries(dentalMarks).filter(([, v]) => v === "LOOSE").map(([k]) => k).join(", ") || "None"}, Missing: {Object.entries(dentalMarks).filter(([, v]) => v === "MISSING").map(([k]) => k).join(", ") || "None"}
            </Typography>

            {/* Systemic Reviews */}
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>3. Review of Systems:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>CVS:</strong> {cvsNad ? "NAD" : `HTN: ${cvsHtn ? (cvsHtnStatus || "Yes") : "No"}, IHD: ${cvsIhd ? "Yes" : "No"}, HF: ${cvsHf ? `NYHA ${cvsHfNyha}` : "No"}, Arrhythmias: ${cvsArrhythmias || "Nil"}, Valvular: ${cvsValvular || "Nil"}, LVEF: ${cvsLvef}%, Heart: ${cvsAuscHeart}${cvsOthers ? ` | Other CVS: ${cvsOthers}` : ""}`}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Respiratory:</strong> {respNad ? "NAD" : `Asthma: ${respAsthma ? "Yes" : "No"}, COPD: ${respCopd ? "Yes" : "No"}, Cough: ${respCough ? "Yes" : "No"}, Dyspnea: ${respDyspnea ? "Yes" : "No"}, URI: ${respRecentUri ? "Yes" : "No"}, Lungs: ${cvsAuscLungs}${respOthers ? ` | Other Resp: ${respOthers}` : ""}`}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Neuro / GI / Renal / Endocrine:</strong> Neuro: {neuroMental} | GI: {giNad ? "NAD" : `${giGerd ? "GERD" : ""}${giOthers ? ` Other: ${giOthers}` : ""}`} | Renal: {renalNad ? "NAD" : `${renalCrf ? `CKD (CCr: ${renalCcr || "N/A"})` : ""}${renalOthers ? ` Other: ${renalOthers}` : ""}`} | Endocrine: {endoNad ? "NAD" : `${endoDm ? `DM ${endoDmType} (HbA1c ${endoHba1c}%)` : ""}${endoLmp ? ` LMP: ${endoLmp}` : ""}${endoOthers ? ` Other: ${endoOthers}` : ""}`}
            </Typography>

            {/* 4. Live CPOE Diagnostic Orders & Results */}
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>4. Laboratory & Radiology Diagnostic Investigations:</Typography>
            {(cbcHb || coagInr || chemCr || chemNa || cxrFindings || ecgFindings || otherLabs || otherInvestigations) && (
              <Box sx={{ mb: 1.5, p: 1, border: "1px dashed #666", bgcolor: "#fcfcfc" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: "block", textTransform: "uppercase", color: "#333", mb: 0.5 }}>
                  Documented Pre-Operative Diagnostic Baseline Values:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.82rem", lineHeight: 1.5 }}>
                  {[
                    cbcHb && `Hb: ${cbcHb} g/dL`,
                    cbcWbc && `WBC: ${cbcWbc} /µL`,
                    cbcHct && `Hct: ${cbcHct}%`,
                    cbcPlt && `Plt: ${cbcPlt} k/µL`,
                    coagPt && `PT: ${coagPt}s`,
                    coagInr && `INR: ${coagInr}`,
                    coagAptt && `APTT: ${coagAptt}s`,
                    chemNa && `Na: ${chemNa} mEq/L`,
                    chemK && `K: ${chemK} mEq/L`,
                    chemCr && `Cr: ${chemCr} mg/dL`,
                    chemBun && `BUN: ${chemBun} mg/dL`,
                    chemGlu && `Glucose: ${chemGlu} mg/dL`,
                    cxrFindings && `CXR: ${cxrFindings}`,
                    ecgFindings && `ECG: ${ecgFindings}`,
                    otherLabs && `Other Labs: ${otherLabs}`,
                    otherInvestigations && `Other Diagnostics: ${otherInvestigations}`,
                  ].filter(Boolean).join(" | ")}
                </Typography>
              </Box>
            )}
            {cpoeOrders.length > 0 && (
              <Table size="small" sx={{ mb: 1.5, border: "1px solid #000" }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                    <TableCell sx={{ border: "1px solid #000" }}><strong>Order #</strong></TableCell>
                    <TableCell sx={{ border: "1px solid #000" }}><strong>Department</strong></TableCell>
                    <TableCell sx={{ border: "1px solid #000" }}><strong>Service / Test Name</strong></TableCell>
                    <TableCell sx={{ border: "1px solid #000" }}><strong>Status</strong></TableCell>
                    <TableCell sx={{ border: "1px solid #000" }}><strong>Key Result / Impression</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cpoeOrders.slice(0, 10).map((o) => (
                    <TableRow key={o.id}>
                      <TableCell sx={{ border: "1px solid #000" }}>{o.order_no}</TableCell>
                      <TableCell sx={{ border: "1px solid #000" }}>{o.type}</TableCell>
                      <TableCell sx={{ border: "1px solid #000" }}>{o.service_name}</TableCell>
                      <TableCell sx={{ border: "1px solid #000" }}>{o.status}</TableCell>
                      <TableCell sx={{ border: "1px solid #000" }}>
                        {o.type === "Laboratory"
                          ? Array.isArray(o.results) && o.results.length > 0
                            ? o.results.slice(0, 3).map((p) => `${p.parameter_name || p.name}: ${p.result_value || p.value} ${p.unit || ""}`).join("; ")
                            : "Parameters available in LIS"
                          : o.impression || o.findings || "Report available in RIS"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {!cpoeOrders.length && !cbcHb && !coagInr && !chemCr && !cxrFindings && !ecgFindings && (
              <Typography variant="body2" sx={{ mb: 1.5, fontStyle: "italic" }}>
                No outside baseline values documented; all routine perioperative tests to be collected per standard admission order.
              </Typography>
            )}

            {/* 5. Anesthesia Plan, Cross-Specialty Clearance & Logistics */}
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>5. Anesthesia Plan, Cross-Specialty Consultation & Blood Logistics:</Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Proposed Anesthesia:</strong> {anesthesiaPlan} &nbsp;&nbsp;|&nbsp;&nbsp; <strong>ASA Physical Status:</strong> ASA {asaClass} {asaEmergency ? "E (Emergency)" : ""}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Cross-Specialty Consultation:</strong> {consultRequired ? `Requested (${consultSpecialty}) — Status: ${consultAccepted || "Pending"} — Details: ${consultReason || "Clearance requested"}` : "No referral required"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Blood Arrangement:</strong> PRBC: {bloodPrbc || 0} U, Whole Blood: {bloodWb || 0} U, FFP: {bloodFfp || 0} U, Platelets: {bloodPlt || 0} U {bloodRemarks ? `| Directives: ${bloodRemarks}` : ""} &nbsp;&nbsp;|&nbsp;&nbsp; <strong>ICU Booking:</strong> {icuBooking ? "Yes (Step-up)" : "No"}
            </Typography>
            {consentRiskDetails && (
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <strong>Informed Consent High-Risk Complications Discussed:</strong> {consentRiskDetails}
              </Typography>
            )}

            {/* 6. Outcome, Orders & Signatures */}
            <Box sx={{ mt: 2, borderTop: "2px solid #000", pt: 1 }}>
              <Typography variant="body2">
                <strong>Fitness Outcome Decision:</strong> <span style={{ fontWeight: 900, textDecoration: "underline" }}>{outcome}</span> &nbsp;&nbsp;&nbsp;&nbsp; <strong>Next Review:</strong> {nextReviewDate || "Cleared"}
              </Typography>
              <Typography variant="body2">
                <strong>Premedications & Nursing Directives:</strong> {suggestedMeds || "Standard pre-op protocol."}
              </Typography>
              <Typography variant="body2">
                <strong>Fasting Instructions:</strong> {optInstructions}
              </Typography>
              <Typography variant="body2">
                <strong>Day-of-Surgery Reassessment:</strong> {reassessmentNotes}
              </Typography>

              {/* Official MIS Dual Signature & Seal Block */}
              <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                <Box>
                  <Typography variant="body2"><strong>Patient / Relative Informed & Agreed:</strong></Typography>
                  <Typography variant="caption" sx={{ display: "block", mt: 3 }}>Signature: ______________________________</Typography>
                  <Typography variant="caption">Relationship & Contact: ___________________</Typography>
                </Box>
                <Box sx={{ textAlign: "center", border: "1px dashed #666", p: 1.5, minWidth: 140 }}>
                  <Typography variant="caption" sx={{ color: "#666", fontWeight: 700 }}>
                    HOSPITAL SEAL / STAMP
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="body2"><strong>Attending Anaesthesiologist:</strong></Typography>
                  <Typography variant="caption" sx={{ display: "block" }}>{sessionStorage.getItem("userName") || "Consultant Anaesthesiologist"}</Typography>
                  <Typography variant="caption" sx={{ display: "block", mt: 2 }}>Signature: ______________________________</Typography>
                  <Typography variant="caption">Medical Reg. No. & Date: _________________</Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
